/**
 * Malayalam. A partial of `en` — anything not translated here renders the
 * English, so this file can lag behind without the page breaking.
 *
 * Loan words are kept where Malayalam speakers already use them ("ബുക്കിംഗ്",
 * "ഡാഷ്‌ബോർഡ്") rather than coined into Sanskritic equivalents nobody says.
 *
 * Not reviewed by a native speaker.
 */
import type { Phrase } from './types';
import type { en } from './en';
import { bookingMl } from './site/booking.ml';
import { coreMl } from './site/core.ml';
import { exploreMl } from './site/explore.ml';
import { homeMl } from './site/home.ml';
import { pagesMl } from './site/pages.ml';
import { staffMl } from './site/staff.ml';
import { venueMl } from './site/venue.ml';

const base: Partial<Record<keyof typeof en, Phrase>> = {
  // ------------------------------------------------------------------ common
  appName: 'Aghosha',
  save: 'സേവ് ചെയ്യുക',
  saving: 'സേവ് ചെയ്യുന്നു…',
  cancel: 'റദ്ദാക്കുക',
  close: 'അടയ്ക്കുക',
  delete: 'ഇല്ലാതാക്കുക',
  remove: 'നീക്കം ചെയ്യുക',
  edit: 'തിരുത്തുക',
  add: 'ചേർക്കുക',
  back: 'തിരികെ',
  next: 'അടുത്തത്',
  previous: 'മുമ്പത്തേത്',
  search: 'തിരയുക',
  clear: 'മായ്ക്കുക',
  apply: 'പ്രയോഗിക്കുക',
  retry: 'വീണ്ടും ശ്രമിക്കുക',
  loading: 'ലോഡ് ചെയ്യുന്നു…',
  confirm: 'സ്ഥിരീകരിക്കുക',
  yes: 'അതെ',
  no: 'അല്ല',
  optional: 'നിർബന്ധമല്ല',
  today: 'ഇന്ന്',
  date: 'തീയതി',
  time: 'സമയം',
  status: 'നില',
  actions: 'നടപടികൾ',
  name: 'പേര്',
  email: 'ഇമെയിൽ',
  phone: 'മൊബൈൽ നമ്പർ',
  password: 'പാസ്‌വേഡ്',
  venue: 'വേദി',
  venues: 'വേദികൾ',
  allVenues: 'എല്ലാ വേദികളും',
  guests: 'അതിഥികൾ',
  price: 'വില',
  total: 'ആകെ',
  notSet: 'നൽകിയിട്ടില്ല',

  // ---------------------------------------------------------------- language
  language: 'ഭാഷ',
  languageAutomatic: 'സ്വയമേവ',
  languageAutomaticHint: 'ബ്രൗസറിലെ ഭാഷ ഉപയോഗിക്കും',

  // --------------------------------------------------------------------- nav
  navMyBookings: 'എന്റെ ബുക്കിംഗുകൾ',
  navAdmin: 'അഡ്മിൻ',
  navNotifications: 'അറിയിപ്പുകൾ',
  navUnread: { one: '{count} വായിക്കാത്തത്', other: '{count} വായിക്കാത്തവ' },
  navOpenMenu: 'മെനു തുറക്കുക',
  navCloseMenu: 'മെനു അടയ്ക്കുക',
  navLightMode: 'ലൈറ്റ് മോഡ്',
  navDarkMode: 'ഡാർക്ക് മോഡ്',
  navToLight: 'ലൈറ്റ് മോഡിലേക്ക് മാറുക',
  navToDark: 'ഡാർക്ക് മോഡിലേക്ക് മാറുക',

  // --------------------------------------------------------------- user menu
  menuAccount: 'അക്കൗണ്ട്: {name}',
  menuBookingCalendar: 'ബുക്കിംഗ് കലണ്ടർ',
  menuManagerDashboard: 'മാനേജർ ഡാഷ്‌ബോർഡ്',
  menuFavorites: 'പ്രിയപ്പെട്ടവ',
  menuEditDetails: 'വിവരങ്ങൾ തിരുത്തുക',
  favLoginToSave: 'വേദികൾ സേവ് ചെയ്യാൻ ലോഗിൻ ചെയ്യുക',
  favSaveFailed: 'പ്രിയപ്പെട്ടവ പുതുക്കാനായില്ല. വീണ്ടും ശ്രമിക്കുക.',
  menuLogout: 'ലോഗ് ഔട്ട്',
  menuDeleteAccount: 'അക്കൗണ്ട് ഇല്ലാതാക്കുക',
  menuLogoutConfirmTitle: 'സൈൻ ഔട്ട് ചെയ്യണോ?',
  menuLogoutConfirmBody: 'ബുക്കിംഗുകൾ കാണാൻ വീണ്ടും സൈൻ ഇൻ ചെയ്യേണ്ടി വരും.',
  menuLogoutConfirmAction: 'സൈൻ ഔട്ട്',

  // ------------------------------------------------------------------ footer
  footerBrowseVenues: 'വേദികൾ കാണുക',
  footerMyBookings: 'എന്റെ ബുക്കിംഗുകൾ',
  footerNotifications: 'അറിയിപ്പുകൾ',
  footerMyVenues: 'എന്റെ വേദികൾ',
  footerAdminVenues: 'വേദികൾ',
  footerCopyright: '© {year} Aghosha.',

  // --------------------------------------------------------------- not found
  notFoundTitle: 'പേജ് കണ്ടെത്തിയില്ല',
  notFoundBody: 'ഈ ലിങ്കിൽ പേജൊന്നുമില്ല.',

  // -------------------------------------------------------- booking calendar
  calFullDay: 'മുഴുവൻ ദിവസം',
  calHalfDay: 'അര ദിവസം',
  calHourly: 'മണിക്കൂർ കണക്കിൽ',
  calBlocked: 'ബ്ലോക്ക് ചെയ്തത്',
  calBooked: 'ബുക്ക് ചെയ്തത്',
  calRequested: 'അഭ്യർത്ഥിച്ചത് — സ്ഥിരീകരിച്ചിട്ടില്ല',
  calBookingCount: { one: '{count} ബുക്കിംഗ്', other: '{count} ബുക്കിംഗുകൾ' },
  calNoBookings: 'ബുക്കിംഗുകളില്ല',
  calJumpTo: 'മാസം തിരഞ്ഞെടുക്കുക',
  calPrevMonth: 'മുൻ മാസം',
  calNextMonth: 'അടുത്ത മാസം',
  calPrevYear: 'മുൻ വർഷം',
  calNextYear: 'അടുത്ത വർഷം',
  calPickDay: 'ബുക്കിംഗുകൾ കാണാൻ നിറമുള്ള ഒരു ദിവസം തിരഞ്ഞെടുക്കുക.',
  calDayFree: 'ഈ ദിവസം ബുക്കിംഗുകളൊന്നുമില്ല.',

  // ------------------------------------------------------------ status pills
  statusConfirmed: 'സ്ഥിരീകരിച്ചു',
  statusPending: 'അഭ്യർത്ഥിച്ചത്',
  statusNewRequest: 'പുതിയ അഭ്യർത്ഥന',
  statusAwaiting: 'സ്ഥിരീകരണം കാത്തിരിക്കുന്നു',
  statusHintNewRequest: 'ഒരു അതിഥി ഈ സമയം ആവശ്യപ്പെട്ടു. സ്ഥിരീകരിക്കുകയോ നിരസിക്കുകയോ ചെയ്യുക.',
  statusHintAwaiting: 'വേദിക്ക് അയച്ചു. അവർ സ്ഥിരീകരിക്കുകയോ നിരസിക്കുകയോ ചെയ്യും.',
  statusHintConfirmed: 'വേദി സ്വീകരിച്ചു. ഈ ബുക്കിംഗിനായി സമയം മാറ്റിവച്ചിരിക്കുന്നു.',
  statusHintCompleted: 'പരിപാടിയുടെ തീയതി കഴിഞ്ഞു.',
  statusHintCancelled: 'റദ്ദാക്കി അല്ലെങ്കിൽ നിരസിച്ചു. സമയം വീണ്ടും ലഭ്യമാണ്.',
  statusHintExpired: 'പരിപാടിക്ക് മുൻപ് മറുപടി ലഭിക്കാത്തതിനാൽ അഭ്യർത്ഥന കാലഹരണപ്പെട്ടു.',
  statusCancelled: 'റദ്ദാക്കി',
  statusCompleted: 'പൂർത്തിയായി',
  statusRefunded: 'പണം തിരികെ നൽകി',
  statusActive: 'സജീവം',
  statusSuspended: 'നിർത്തിവച്ചു',
  statusVerified: 'പരിശോധിച്ചു',
  statusRejected: 'നിരസിച്ചു',
  statusNew: 'പുതിയത്',
  statusContacted: 'ബന്ധപ്പെട്ടു',
  statusClosed: 'അവസാനിപ്പിച്ചു',
  statusHandled: 'കൈകാര്യം ചെയ്തു',
  statusExpired: 'കാലാവധി കഴിഞ്ഞു',

  // -------------------------------------------------------------- venue card
  cardNew: 'പുതിയത്',
  cardDayFree: 'ദിവസം മുഴുവൻ ലഭ്യം',
  cardDayPartial: { one: 'ഭാഗികമായി ബുക്ക് ചെയ്തു · {count} സെഷൻ ലഭ്യം', other: 'ഭാഗികമായി ബുക്ക് ചെയ്തു · {count} സെഷനുകൾ ലഭ്യം' },
  cardFullAc: 'മുഴുവൻ എ.സി.',
  cardParking: 'പാർക്കിംഗ്',
  cardCompare: 'താരതമ്യം',
  cardAdded: 'ചേർത്തു',
  cardCompareAdd: 'താരതമ്യത്തിലേക്ക് ചേർക്കുക',
  cardCompareRemove: 'താരതമ്യത്തിൽ നിന്ന് നീക്കുക',
  cardCompareLimit: 'ഒരേസമയം രണ്ട് വേദികൾ മാത്രം താരതമ്യം ചെയ്യാം. മറ്റൊന്ന് ചേർക്കാൻ ഒന്ന് നീക്കുക.',

  // ------------------------------------------------------------ browse page
  browseSearchPlaceholder: 'വേദിയുടെ പേര്, സ്ഥലം അല്ലെങ്കിൽ നഗരം തിരയുക…',
  browseSearchLabel: 'വേദികൾ തിരയുക',
  browseAvailableOn: 'ഒഴിവുള്ള തീയതി',
  browseAnyDate: 'ഏത് തീയതിയും',
  browseAllTypes: 'എല്ലാ തരങ്ങളും',
  browseParking: 'പാർക്കിംഗ്',
  browsePriceDesc: 'വില: കൂടിയത് → കുറഞ്ഞത്',
  browsePriceAsc: 'വില: കുറഞ്ഞത് → കൂടിയത്',
  browseCapacity: 'ശേഷി',
  browseRating: 'റേറ്റിംഗ്',
  browseClearFilters: 'ഫിൽട്ടറുകൾ മായ്ക്കുക',
  browseMaxPriceRule: 'മണിക്കൂറിന് 0-ൽ കൂടുതലുള്ള തുക നൽകുക',
  browseFinding: 'ഒഴിവുള്ള വേദികൾ തിരയുന്നു…',
  browseLoadFailed: 'വേദികൾ ലോഡ് ചെയ്യാനായില്ല',
  browseVenueCount: { one: '{count} വേദി', other: '{count} വേദികൾ' },
  browseCityCount: { one: '{count} നഗരം', other: '{count} നഗരങ്ങൾ' },
  browseSummary: '{venues}, {cities} — {capacity} അതിഥികൾക്ക് വരെ ഇരിക്കാം.',
  browseSummaryRating: 'ശരാശരി {rating}★ റേറ്റിംഗ്.',
  browsePickMore: {
    one: 'താരതമ്യം ചെയ്യാൻ ഒരെണ്ണം കൂടി തിരഞ്ഞെടുക്കുക',
    other: 'താരതമ്യം ചെയ്യാൻ {count} എണ്ണം കൂടി തിരഞ്ഞെടുക്കുക',
  },

  // ------------------------------------------------------------- venue page
  detailLoginFirst: 'ബുക്കിംഗ് അഭ്യർത്ഥന അയയ്ക്കാൻ ലോഗിൻ ചെയ്യുക',
  detailLinkCopied: 'ലിങ്ക് പകർത്തി',
  detailWhatsappMessage: 'ഹായ്, {name} ബുക്ക് ചെയ്യാൻ താൽപ്പര്യമുണ്ട്. ഒഴിവുണ്ടോ?',
  detailPhotosUpdated: '{date}-ന് ചിത്രങ്ങൾ പുതുക്കി',
  detailNewListing: 'പുതുതായി ചേർത്തത്',
  detailReviewCount: { one: '{count} അഭിപ്രായം', other: '{count} അഭിപ്രായങ്ങൾ' },
  detailRoomCount: { one: '{count} മുറി', other: '{count} മുറികൾ' },
  detailCarCount: { one: '{count} കാർ', other: '{count} കാറുകൾ' },
  detailBikeCount: { one: '{count} ബൈക്ക്', other: '{count} ബൈക്കുകൾ' },
  detailAmenities: 'സൗകര്യങ്ങൾ',
  detailWholeHall: 'വേദി മുഴുവനായി ബുക്ക് ചെയ്യാം.',
  detailPeakTip: 'ശനി, ഞായർ ദിവസങ്ങളിലെ ബുക്കിംഗിന് സാധാരണ നിരക്കിനേക്കാൾ ഇത്രയും കൂടുതലാകും. ഏകദേശ തുകയിൽ ഇത് ഉൾപ്പെട്ടിട്ടുണ്ട്.',
  detailReviews: 'അഭിപ്രായങ്ങൾ',
  detailNoReviews: 'ഇതുവരെ അഭിപ്രായങ്ങളില്ല.',
  detailCall: '{phone} എന്ന നമ്പറിൽ വിളിക്കുക',
  detailNoPhone: 'ഫോൺ നമ്പർ നൽകിയിട്ടില്ല.',
  detailDirections: 'വഴി',
  detailShare: 'പങ്കിടുക',
  detailSimilar: 'സമാനമായ വേദികൾ',
  detailSameBuilding: '{name}-ലെ മറ്റ് വേദികൾ',
  detailSimilarBody:
    '{name}-മായി ഒത്തുനോക്കാൻ ഏതെങ്കിലും കാർഡിലെ "താരതമ്യം" അമർത്തുക.',

  // ----------------------------------------------------------- compare modal
  cmpTitle: 'വേദികൾ താരതമ്യം ചെയ്യുക',
  cmpSubtitle: 'ഓരോ വരിയിലും മികച്ചത് ഹൈലൈറ്റ് ചെയ്തിട്ടുണ്ട്.',
  cmpHourly: 'മണിക്കൂർ നിരക്ക്',
  cmpHalfDay: 'സെഷൻ',
  cmpFullDay: 'മുഴുവൻ ദിവസം',
  cmpGuestRooms: 'അതിഥി മുറികൾ',
  cmpOpen: '{name} തുറക്കുക',

  // ------------------------------------------------------- booking panel etc
  bookHours: { one: '{count} മണിക്കൂർ', other: '{count} മണിക്കൂർ' },
  myQuoteFailed: 'ഈ സമയത്തിന്റെ നിരക്ക് കണക്കാക്കാനായില്ല. മറ്റൊരു സമയം നോക്കൂ.',
  bookDays: { one: '{count} ദിവസം', other: '{count} ദിവസം' },
  bookMinutes: { one: '{count} മിനിറ്റ്', other: '{count} മിനിറ്റ്' },
  bookBuffer: 'വൃത്തിയാക്കാനുള്ള ഇടവേള',
  bookBufferTip: 'ബുക്കിംഗുകൾ കൂട്ടിമുട്ടാതിരിക്കാൻ നിങ്ങളുടെ സമയത്തിന് മുൻപും ശേഷവും മാറ്റിവയ്ക്കുന്ന സമയം',
  bookTotalEstimate: 'ആകെ ഏകദേശ തുക',
  bookNoPayment: 'ഇപ്പോൾ പണം വേണ്ട',
  bookVenueConfirms: 'ആദ്യം വേദി സ്ഥിരീകരിക്കും',

  // enquiry form on the venue page
  enqSend: 'അന്വേഷണം അയയ്ക്കുക',
  enqEventDate: 'പരിപാടിയുടെ തീയതി',
  enqMessagePlaceholder: 'നിങ്ങളുടെ പരിപാടിയെക്കുറിച്ച് വേദിയോട് പറയുക…',
  enqNamePhoneRequired: 'പേരും മൊബൈൽ നമ്പറും നൽകുക',
  enqGuestsRule: 'അതിഥികളുടെ എണ്ണം 0-ഓ അതിൽ കൂടുതലോ ഉള്ള പൂർണ്ണസംഖ്യ ആയിരിക്കണം',
  enqGuestsMax: 'അതിഥികളുടെ എണ്ണം {max}-ൽ കൂടരുത്',
  enqMessageRequired: 'നിങ്ങൾക്ക് എന്താണ് വേണ്ടതെന്ന് വേദിക്ക് മനസ്സിലാകാൻ ഒരു ചെറിയ സന്ദേശം എഴുതുക',
  enqMessageMax: 'സന്ദേശം {max} അക്ഷരങ്ങളിൽ കൂടരുത്',
  enqSent: 'അന്വേഷണം അയച്ചു — വേദി നിങ്ങളെ ബന്ധപ്പെടും.',

  // gallery + panorama
  galPrev: 'മുൻ ചിത്രം',
  galNext: 'അടുത്ത ചിത്രം',
  galPhotoAlt: '{name} — {count}-ൽ {index}-ാമത്തെ ചിത്രം',
  panoAria: '360 ഡിഗ്രി കാഴ്ച — ചുറ്റും കാണാൻ വലിച്ചുനീക്കുക',
  panoDrag: 'ചുറ്റും കാണാൻ വലിച്ചുനീക്കുക',

  // --------------------------------------------------------- my bookings
  myBookingsTitle: 'എന്റെ ബുക്കിംഗുകൾ',
  myBookingsSubtitle: 'ഓരോ അഭ്യർത്ഥനയുടെയും നില കാണാം, തീയതി മാറ്റാം, റദ്ദാക്കാം, പരിപാടിക്ക് ശേഷം വേദിക്ക് അവലോകനം എഴുതാം.',
  myNoBookings: 'ഇതുവരെ ബുക്കിംഗുകളില്ല',
  myNoBookingsBody: 'ഒരു വേദി കണ്ടെത്തി അഭ്യർത്ഥന അയയ്ക്കൂ. അത് ഇവിടെ കാണാം.',
  myReschedule: 'സമയം മാറ്റുക',
  myRescheduledPending: 'മാറ്റി. പുതിയ സമയം വേദി സ്ഥിരീകരിക്കും.',
  myLeaveReview: 'അഭിപ്രായം എഴുതുക',
  myReview: 'അഭിപ്രായം',
  myReviewThanks: 'അഭിപ്രായത്തിന് നന്ദി.',
  myComment: 'അഭിപ്രായം',
  mySubmitReview: 'അഭിപ്രായം പോസ്റ്റ് ചെയ്യുക',
  myStarCount: { one: '{count} നക്ഷത്രം', other: '{count} നക്ഷത്രങ്ങൾ' },
  myCurrentlyAt: 'ഇപ്പോൾ',
  myPickNewSlot: 'താഴെ പുതിയ സമയം തിരഞ്ഞെടുക്കുക.',
  myLoadingAvailability: 'ലഭ്യത ലോഡ് ചെയ്യുന്നു…',
  myVenueClosedToChanges:
    'ഈ വേദി ഇനി ബുക്കിംഗുകൾ സ്വീകരിക്കുന്നില്ല, അതിനാൽ ഇത് മാറ്റാൻ കഴിയില്ല. മാറ്റാനോ റദ്ദാക്കാനോ വേദിയുമായി ബന്ധപ്പെടുക.',
  myNewSlot: 'പുതിയ സമയം',
  myDuration: 'എത്ര സമയം',
  myOldAmount: 'നിലവിലെ തുക',
  myNewAmount: 'പുതിയ സമയത്തിനുള്ള തുക',
  myAmountUp: 'പുതിയ സമയത്തിന് {diff} കൂടുതൽ.',
  myAmountDown: 'പുതിയ സമയത്തിന് {diff} കുറവ്.',
  myAlreadyPaid: 'നിങ്ങൾ ഇതിനകം വേദിക്ക് {paid} നൽകിയിട്ടുണ്ട്; അത് പുതിയ തുകയിൽ കണക്കാക്കും.',
  myReapprovalNote: 'പുതിയ സമയം അന്തിമമാകുന്നതിന് മുൻപ് വേദി വീണ്ടും സ്ഥിരീകരിക്കും.',
  myMoving: 'മാറ്റുന്നു…',
  myConfirmNewSlot: 'പുതിയ സമയം സ്ഥിരീകരിക്കുക',
  myPickSlot: 'ഒരു സമയം തിരഞ്ഞെടുക്കുക',
  myCallVenue: 'വേദിയെ വിളിക്കുക',

  // ------------------------------------------------- വേദിയോട് സമയം ചോദിക്കൽ
  reqAwaitingTitle: 'ഈ ബുക്കിംഗ് വേദി സ്ഥിരീകരിക്കും',
  reqAwaitingBody:
    'മാനേജർ തീയതി നോക്കി സ്ഥിരീകരിക്കും. വേഗം സ്ഥിരീകരിച്ചു കിട്ടാൻ അവരെ വിളിക്കുകയോ മെസ്സേജ് അയയ്ക്കുകയോ ചെയ്യാം.',
  reqRequestNo: 'അഭ്യർത്ഥന നമ്പർ',
  reqEstimate: 'ആകെ ഏകദേശ തുക',
  reqPayAtVenue: 'വേദിയിൽ അടയ്ക്കാം — ഓൺലൈനിൽ ഒന്നും അടയ്ക്കേണ്ടതില്ല.',
  reqNextTitle: 'അടുത്തത് എന്ത്',
  reqNext1: '{venue} നിങ്ങളുടെ അഭ്യർത്ഥന പരിശോധിച്ച് സ്ഥിരീകരിക്കുകയോ നിരസിക്കുകയോ ചെയ്യും.',
  reqNext2: 'അവർ തീരുമാനിച്ചാലുടൻ "എന്റെ ബുക്കിംഗുകൾ"-ലും ഫോണിലും അറിയിപ്പ് ലഭിക്കും.',
  reqNext3: 'സ്ഥിരീകരിച്ചാൽ, മുഴുവൻ തുകയും വേദിയിൽ നേരിട്ട് നൽകുക. ഓൺലൈനായോ മുൻകൂറായോ ഒന്നും അടയ്ക്കേണ്ടതില്ല.',
  reqQuestions: 'ഈ അഭ്യർത്ഥനയെക്കുറിച്ച് സംശയങ്ങളുണ്ടോ?',
  reqCallVenue: 'വേദിയെ വിളിക്കുക',
  reqWhatsapp: 'വാട്ട്‌സ്ആപ്പ്',
  reqWhatsappMessage:
    'ഹായ്, {slot} സമയത്തേക്ക് {venue} ബുക്ക് ചെയ്യാൻ അഭ്യർത്ഥന അയച്ചിട്ടുണ്ട് (അഭ്യർത്ഥന #{id}). ദയവായി സ്ഥിരീകരിക്കാമോ?',
  reqViewBookings: 'എന്റെ ബുക്കിംഗുകൾ കാണുക',
  reqNotHeldNote: 'വേദി സ്ഥിരീകരിക്കുന്നതുവരെ ഈ സമയം നിങ്ങൾക്കായി മാറ്റിവയ്ക്കില്ല.',
  reqAlreadySent: 'ഈ സമയത്തിന് നിങ്ങൾ മുൻപേ അഭ്യർത്ഥന അയച്ചിട്ടുണ്ട് — അത് ഇപ്പോഴും വേദിയുടെ പക്കലുണ്ട്.',

  // ------------------------------------------------------------ cancellation
  cxlTitle: 'ബുക്കിംഗ് റദ്ദാക്കുക',
  cxlStartsIn: 'തുടങ്ങാൻ ബാക്കി',
  cxlPolicyIntro: 'പണം വേദിക്ക് നേരിട്ടാണ് നൽകുന്നത്, Aghosha വഴിയല്ല. നിങ്ങൾ പണം നൽകിയിട്ടുണ്ടെങ്കിൽ, പരിപാടിക്ക് എത്ര സമയം മുൻപ് റദ്ദാക്കുന്നു എന്നതനുസരിച്ചാണ് വേദി തിരികെ നൽകുക:',
  cxlPercentBack: '{percent}% തിരികെ',
  cxlAppliesToYou: 'നിങ്ങൾക്ക് ബാധകം',
  cxlNoRefundWindow:
    'ഈ ബുക്കിംഗിന് പണം തിരികെ കിട്ടാനുള്ള സമയം കഴിഞ്ഞു — റദ്ദാക്കിയാൽ പണമൊന്നും തിരികെ കിട്ടില്ല.',
  cxlSlotReleased: 'ഈ സമയം ഉടൻ ഒഴിവാകും, മറ്റാർക്കും ബുക്ക് ചെയ്യാം.',
  cxlCannotUndo: 'റദ്ദാക്കിയാൽ പിന്നെ തിരിച്ചെടുക്കാനാവില്ല.',
  cxlKeepBooking: 'ബുക്കിംഗ് നിലനിർത്തുക',
  cxlKeepRequest: 'അഭ്യർത്ഥന നിലനിർത്തുക',
  cxlWithdrawTitle: 'അഭ്യർത്ഥന പിൻവലിക്കുക',
  cxlNothingAgreed: 'ഒന്നും ഉറപ്പിച്ചിട്ടില്ല, പണവും അടച്ചിട്ടില്ല',
  cxlCancelling: 'റദ്ദാക്കുന്നു…',
  cxlDoneRefund: 'റദ്ദാക്കി. വേദി {amount} നിങ്ങൾക്ക് നേരിട്ട് തിരികെ നൽകും.',
  cxlDoneNoRefund: 'ബുക്കിംഗ് റദ്ദാക്കി. തിരികെ നൽകാൻ ഒന്നുമില്ല.',
  refundTier48: 'പരിപാടിക്ക് 48 മണിക്കൂറോ അതിൽ കൂടുതലോ മുൻപ്',
  refundTier12: '12 മുതൽ 48 മണിക്കൂർ വരെ മുൻപ്',
  refundTier0: '12 മണിക്കൂറിൽ കുറവ് സമയം മുൻപ്',
  refundAlreadyStarted: 'തുടങ്ങിക്കഴിഞ്ഞു',
  refundDays: { one: '{count} ദിവസം', other: '{count} ദിവസം' },
  refundHours: { one: '{count} മണിക്കൂർ', other: '{count} മണിക്കൂർ' },
  refundMinutes: { one: '{count} മിനിറ്റ്', other: '{count} മിനിറ്റ്' },

  // ------------------------------------------------------- shared components
  selectPlaceholder: 'തിരഞ്ഞെടുക്കുക…',
  skLoadingVenues: 'വേദികൾ ലോഡ് ചെയ്യുന്നു',
  skLoadingVenue: 'വേദി ലോഡ് ചെയ്യുന്നു',
  pwShow: 'പാസ്‌വേഡ് കാണിക്കുക',
  pwHide: 'പാസ്‌വേഡ് മറയ്ക്കുക',
  dfSelectDate: 'ഒരു തീയതി തിരഞ്ഞെടുക്കുക',
  dfChooseDate: 'തീയതി തിരഞ്ഞെടുക്കുക',
  dfChooseTime: 'സമയം തിരഞ്ഞെടുക്കുക',
  dfHour: 'മണിക്കൂർ',
  dfMinute: 'മിനിറ്റ്',
  dfMeridiem: 'AM/PM',

  // -------------------------------------------------------------- validation
  valRequired: '{label} നിർബന്ധമാണ്',
  valNameChars: "{label} എന്നതിൽ അക്ഷരങ്ങൾ, അക്കങ്ങൾ, സ്പേസ്, . ' - എന്നിവ മാത്രമേ ഉപയോഗിക്കാവൂ",
  valMobileRequired: 'മൊബൈൽ നമ്പർ നിർബന്ധമാണ്',
  valPhoneDigits: 'കൃത്യം 10 അക്കങ്ങൾ വേണം',
  valEmailInvalid: 'ശരിയായ ഒരു ഇമെയിൽ വിലാസം നൽകുക',
  valPasswordRequired: 'പാസ്‌വേഡ് നിർബന്ധമാണ്',
  valPasswordMin: {
    one: 'ചുരുങ്ങിയത് {count} അക്ഷരം ഉപയോഗിക്കുക',
    other: 'ചുരുങ്ങിയത് {count} അക്ഷരങ്ങൾ ഉപയോഗിക്കുക',
  },
  valIdDigits: {
    one: '{label}-ൽ {count} അക്കം വേണം',
    other: '{label}-ൽ {count} അക്കങ്ങൾ വേണം',
  },
  valIdChars: {
    one: '{label}-ൽ {count} അക്ഷരം വേണം',
    other: '{label}-ൽ {count} അക്ഷരങ്ങൾ വേണം',
  },

  // -------------------------------------------------------------------- auth
  authWelcomeBack: 'വീണ്ടും സ്വാഗതം',
  authCreateAccount: 'അക്കൗണ്ട് ഉണ്ടാക്കുക',
  authLoginSubtitle: 'ബുക്കിംഗുകൾ കൈകാര്യം ചെയ്യാൻ സൈൻ ഇൻ ചെയ്യുക.',
  authLogin: 'ലോഗിൻ',
  authConfirmNumberTitle: 'നിങ്ങളുടെ മൊബൈൽ നമ്പർ സ്ഥിരീകരിക്കുക',
  authConfirmNumberBody: '{phone} എന്ന നമ്പറിലേക്ക് SMS വഴി ഒരു കോഡ് അയച്ചിട്ടുണ്ട്. ആദ്യ ലോഗിൻ പൂർത്തിയാക്കാൻ അത് നൽകുക.',
  authCodeLabel: 'SMS-ൽ ലഭിച്ച കോഡ്',
  authCodeExpiresIn: '{time} കൂടി ഈ കോഡ് ഉപയോഗിക്കാം',
  authCodeExpired: 'കോഡിന്റെ കാലാവധി കഴിഞ്ഞു — പുതിയ കോഡിനായി "കോഡ് വീണ്ടും അയയ്ക്കുക" അമർത്തുക',
  authConfirmAndSignIn: 'സ്ഥിരീകരിച്ച് ലോഗിൻ ചെയ്യുക',
  authResendCode: 'കോഡ് വീണ്ടും അയയ്ക്കുക',
  authCodeResent: 'പുതിയ കോഡ് അയച്ചു.',
  authSignup: 'രജിസ്റ്റർ ചെയ്യുക',
  authLoggingIn: 'ലോഗിൻ ചെയ്യുന്നു…',
  authRememberMe: 'എന്റെ നമ്പർ ഓർത്തുവയ്ക്കുക',
  authPhonePlaceholder: 'ഉദാ. 9000000005',
  authPhoneHint: '10 അക്ക മൊബൈൽ നമ്പർ',
  authWelcomeName: 'സ്വാഗതം, {name}!',
  authManagerPending:
    'അക്കൗണ്ട് ഉണ്ടാക്കി. ഒരു അഡ്മിൻ അംഗീകരിച്ചാൽ വേദികൾ ലിസ്റ്റ് ചെയ്യാം.',
  authOtpIntro: 'ഈ നമ്പറിലേക്ക് 6 അക്ക കോഡ് SMS ആയി അയയ്ക്കും.',
  authSendOtp: 'കോഡ് അയയ്ക്കുക',
  authEnterOtp: 'SMS-ൽ ലഭിച്ച കോഡ്',
  authOtpPlaceholder: '6 അക്ക കോഡ്',
  authOtpIs: 'നിങ്ങളുടെ കോഡ് {code} ആണ്',
  authOtpSent: '{phone} എന്ന നമ്പറിലേക്ക് കോഡ് അയച്ചു',
  authOtpStillLive: '{phone} എന്ന നമ്പറിലേക്ക് അയച്ച കോഡ് ഇപ്പോഴും ഉപയോഗിക്കാം. അത് താഴെ നൽകുക.',
  authVerify: 'നമ്പർ ഉറപ്പാക്കുക',
  authResend: 'കോഡ് വീണ്ടും അയയ്ക്കുക',
  authOtpExpiresIn: '{time} കൂടി ഈ കോഡ് ഉപയോഗിക്കാം',
  authOtpExpired: 'കോഡിന്റെ കാലാവധി കഴിഞ്ഞു — പുതിയ കോഡിനായി "കോഡ് വീണ്ടും അയയ്ക്കുക" അമർത്തുക',
  authChangeNumber: 'നമ്പർ മാറ്റുക',
  authVerified: 'ഉറപ്പാക്കി',
  authAddress: 'വിലാസം',
  authAddressPlaceholder: 'തെരുവ്, നഗരം',
  authCreateAccountAction: 'അക്കൗണ്ട് ഉണ്ടാക്കുക',
  authIdentityCheck: 'തിരിച്ചറിയൽ പരിശോധന',
  authIdentityCheckBody:
    'അക്കൗണ്ട് സജീവമാകുന്നതിന് മുൻപ് ഒരു അഡ്മിൻ നിങ്ങളുടെ തിരിച്ചറിയൽ രേഖ പരിശോധിക്കും.',
  authIdType: 'തിരിച്ചറിയൽ രേഖയുടെ തരം',
  authIdNumber: 'രേഖയുടെ നമ്പർ',
  authIdNumberPlaceholder: 'രേഖയിൽ ഉള്ളതുപോലെ',
  authIdUpload: 'രേഖ അപ്‌ലോഡ് ചെയ്യുക (PDF, JPG അല്ലെങ്കിൽ PNG · പരമാവധി 10 MB)',
  authIdUploaded: 'തിരിച്ചറിയൽ രേഖ അപ്‌ലോഡ് ചെയ്തു',
  authIdRequired: 'തിരിച്ചറിയൽ രേഖ അപ്‌ലോഡ് ചെയ്ത് അതിന്റെ നമ്പർ നൽകുക',
  authUploading: 'അപ്‌ലോഡ് ചെയ്യുന്നു…',

  // suspension + appeals
  authSuspendedTitle: 'നിങ്ങളുടെ അക്കൗണ്ട് താൽക്കാലികമായി നിർത്തിവച്ചിരിക്കുന്നു',
  authSuspendedReason: 'നൽകിയ കാരണം:',
  authSuspendedNoReason:
    'കാരണം രേഖപ്പെടുത്തിയിട്ടില്ല. താഴെ ഒരു അപ്പീൽ അയയ്ക്കുക, ഒരു അഡ്മിൻ മറുപടി നൽകും.',
  authSuspendedOn: '{date}-ന് നിർത്തിവച്ചു',
  authAppealDeclined: 'നിങ്ങളുടെ കഴിഞ്ഞ അപ്പീൽ നിരസിച്ചു',
  authAppealPending:
    'ഒരു അഡ്മിൻ നിങ്ങളുടെ അപ്പീൽ പരിശോധിക്കുകയാണ്. അംഗീകരിച്ചാലുടൻ ലോഗിൻ ചെയ്യാം.',
  authAppealPendingSent:
    '{date}-ന് അയച്ച നിങ്ങളുടെ അപ്പീൽ ഒരു അഡ്മിൻ പരിശോധിക്കുകയാണ്. അംഗീകരിച്ചാലുടൻ ലോഗിൻ ചെയ്യാം.',
  authAppealLabel: 'അഡ്മിനുള്ള നിങ്ങളുടെ സന്ദേശം',
  authAppealPlaceholder: 'നിർത്തിവയ്ക്കൽ എന്തുകൊണ്ട് പിൻവലിക്കണമെന്ന് എഴുതുക (കുറഞ്ഞത് 10 അക്ഷരങ്ങൾ)',
  authSupportingDoc: 'തെളിവിനുള്ള രേഖ',
  authSupportingDocHint: 'നിർബന്ധമല്ല — PDF, JPG അല്ലെങ്കിൽ PNG',
  authSupportingDocTip: 'സാധാരണ, പുതുക്കിയ ലൈസൻസോ കൂടുതൽ വ്യക്തമായ ഐഡിയോ നൽകിയാൽ ഇത് പരിഹരിക്കാം.',
  authDocAttached: 'രേഖ ചേർത്തു',
  authSendAppeal: 'അപ്പീൽ അയയ്ക്കുക',
  authSending: 'അയയ്ക്കുന്നു…',
  authAppeal: 'ഈ നിർത്തിവയ്ക്കലിനെതിരെ അപ്പീൽ നൽകുക',
  authAppealAgain: 'വീണ്ടും അപ്പീൽ നൽകുക',
  authBackToLogin: 'ലോഗിനിലേക്ക് മടങ്ങുക',
  authMediaKicker: 'Aghosha',
  authQuoteA: 'ഇടം കണ്ടെത്തൂ.',
  authQuoteB: 'നിമിഷം ഒരുക്കൂ.',
  authMediaBody: 'പരിശോധിച്ച വേദികൾ, യഥാർത്ഥ ലഭ്യത, ഓൺലൈനായി പണമടയ്ക്കേണ്ടതില്ല.',
  authAccountTabs: 'അക്കൗണ്ട്',
  authRolePickLabel: 'എനിക്ക് വേണ്ടത്',
  authRoleCustomerTitle: 'വേദികൾ ബുക്ക് ചെയ്യുക',
  authRoleCustomerHint: 'ഞാൻ ആസൂത്രണം ചെയ്യുന്ന പരിപാടികൾക്ക്',
  authRoleManagerTitle: 'എന്റെ വേദി ലിസ്റ്റ് ചെയ്യുക',
  authRoleManagerHint: 'ഞാൻ നിയന്ത്രിക്കുന്ന വേദികൾക്ക്',

  // ------------------------------------------------------------------ contact
  ctHours: 'തിങ്കൾ–ശനി, രാവിലെ 9:00 – വൈകുന്നേരം 7:00',
  ctHoursLabel: 'പ്രവർത്തന സമയം',
  ctHeroLead: 'ഞങ്ങൾ ഇവിടെയുണ്ട്',
  ctHeroAccent: 'സഹായിക്കാൻ.',
  ctHeroBody: 'വേദി ബുക്ക് ചെയ്യുന്നതിനെക്കുറിച്ചോ നിങ്ങളുടെ വേദി ചേർക്കുന്നതിനെക്കുറിച്ചോ സംശയമുണ്ടോ? വിളിക്കൂ, ഇമെയിൽ ചെയ്യൂ, അല്ലെങ്കിൽ താഴെ സന്ദേശം അയയ്ക്കൂ.',
  ctReachUs: 'ഞങ്ങളെ ബന്ധപ്പെടാൻ',
  ctOffice: 'ഓഫീസ്',
  ctPhone: 'ഫോൺ',
  ctGetDirections: 'വഴി കാണുക',
  ctSendMessage: 'ഞങ്ങൾക്ക് സന്ദേശം അയയ്ക്കുക',
  ctSubject: 'വിഷയം',
  ctSubjectHint: 'എങ്ങനെ സഹായിക്കാം?',
  ctMessageHint: 'എന്ത് സഹായമാണ് വേണ്ടത്? ബുക്കിംഗിനാണെങ്കിൽ തീയതിയും അതിഥികളുടെ എണ്ണവും ചേർക്കുക.',
  ctNameMessageRequired: 'പേരും സന്ദേശവും നൽകുക',
  ctThanks: 'സന്ദേശം അയച്ചു. ഞങ്ങൾ ഉടൻ ബന്ധപ്പെടും.',

  // ------------------------------------------------------------------ privacy
  // Only the chrome is translated. The policy clauses stay in English — see the
  // note in PrivacyPage.tsx: a paraphrased retention or consent clause would
  // promise something the reviewed text does not.
  privTitle: 'സ്വകാര്യത',
  privUpdated: 'അവസാനം പുതുക്കിയത് {date}',
  privHeroLead: 'നിങ്ങളുടെ വിവരങ്ങൾ,',
  privHeroAccent: 'അതിൽ കൂടുതലൊന്നുമില്ല.',
  privHeroBody:
    'ഒരു വേദി ബുക്ക് ചെയ്യുമ്പോഴോ ചേർക്കുമ്പോഴോ Aghosha എന്തൊക്കെ വിവരങ്ങൾ ശേഖരിക്കുന്നു, അവ ആരൊക്കെ കാണും, അവ എങ്ങനെ നീക്കം ചെയ്യിക്കാം.',
  privContactHeading: 'സ്വകാര്യതയെക്കുറിച്ച് ഞങ്ങളെ ബന്ധപ്പെടുക',
  privContactBody:
    'ഈ നയത്തെക്കുറിച്ച് സംശയമുണ്ടെങ്കിലോ, ഞങ്ങളുടെ പക്കലുള്ള നിങ്ങളുടെ വിവരങ്ങളുടെ പകർപ്പോ തിരുത്തലോ നീക്കം ചെയ്യലോ വേണമെങ്കിലോ ഞങ്ങളുടെ പരാതി പരിഹാര ഓഫീസർക്ക് എഴുതുക.',
  privUseContactForm: 'ബന്ധപ്പെടാനുള്ള ഫോം ഉപയോഗിക്കുക',

  // ------------------------------------------------------------ notifications
  notifSubtitle: 'ബുക്കിംഗ് വിവരങ്ങൾ, അംഗീകാരങ്ങൾ, പണം തിരികെ നൽകൽ.',
  notifEmpty: 'ഇതുവരെ അറിയിപ്പുകളൊന്നുമില്ല',
  notifEmptyBody: 'പുതിയ അറിയിപ്പുകൾ ഇവിടെ കാണാം.',

  // ------------------------------------------------------------ account modals
  epCurrentRequired: 'പാസ്‌വേഡ് മാറ്റാൻ നിലവിലുള്ള പാസ്‌വേഡ് നൽകുക',
  epSaved: 'വിവരങ്ങൾ സേവ് ചെയ്തു',
  epPhoneFixed: 'നിങ്ങളുടെ മൊബൈൽ നമ്പർ മാറ്റാനാവില്ല.',
  epChangePassword: 'പാസ്‌വേഡ് മാറ്റുക',
  epCurrentPassword: 'നിലവിലുള്ള പാസ്‌വേഡ്',
  epNewPassword: 'പുതിയ പാസ്‌വേഡ്',
  epNewPasswordHint: 'ഇപ്പോഴത്തെ പാസ്‌വേഡ് തുടരാൻ ഇത് ഒഴിച്ചിടുക',
  epSaveChanges: 'മാറ്റങ്ങൾ സേവ് ചെയ്യുക',

  daCannotUndo: 'ഇത് പിന്നീട് പിൻവലിക്കാനാവില്ല.',
  daBody:
    'നിങ്ങളുടെ പേര്, ഇമെയിൽ, വിലാസം, മൊബൈൽ നമ്പർ എന്നിവ മായ്ക്കും. പിന്നീട് സൈൻ ഇൻ ചെയ്യാനാവില്ല.',
  daManagerNote: 'നിങ്ങളുടെ വേദികൾ സൈറ്റിൽ നിന്ന് നീക്കം ചെയ്യും.',
  daCancelFirst: 'അക്കൗണ്ട് ഇല്ലാതാക്കുന്നതിന് മുൻപ് വരാനിരിക്കുന്ന ബുക്കിംഗുകൾ റദ്ദാക്കണം.',
  daPastBookings: 'പഴയ ബുക്കിംഗുകൾ വേദിയുടെ രേഖകളിൽ തുടരും, പക്ഷേ അവയിൽ നിങ്ങളുടെ വിവരങ്ങൾ ഉണ്ടാകില്ല.',
  daTypeToConfirm: 'സ്ഥിരീകരിക്കാൻ {word} എന്ന് ടൈപ്പ് ചെയ്യുക',
  daKeepAccount: 'അക്കൗണ്ട് നിലനിർത്തുക',
  daDeleting: 'ഇല്ലാതാക്കുന്നു…',
  daDeleted: 'അക്കൗണ്ട് ഇല്ലാതാക്കി',

  // ------------------------------------------------------------ slot picker
  slotMorning: 'രാവിലെ',
  slotAfternoon: 'ഉച്ചയ്ക്ക്',
  slotEvening: 'വൈകുന്നേരം',
  slotBySession: 'സെഷൻ അനുസരിച്ച്',
  slotByHour: 'മണിക്കൂർ അനുസരിച്ച്',
  slotByDays: 'ഒന്നിലധികം ദിവസം',
  slotFirstDay: 'ആദ്യ ദിവസം',
  slotLastDay: 'അവസാന ദിവസം',
  slotPickLastDay: 'അവസാന ദിവസം തിരഞ്ഞെടുക്കുക',
  slotDaysHint: 'ഓരോ ദിവസവും {from} – {to}. ഒരു ബുക്കിംഗിൽ പരമാവധി {max} ദിവസം. സ്ഥിരീകരിക്കുമ്പോൾ വേദി ഓരോ ദിവസവും പരിശോധിക്കും.',
  slotFirstDayTaken: 'ആദ്യ ദിവസത്തിന്റെ ഒരു ഭാഗം ഇതിനകം ബുക്ക് ചെയ്തിട്ടുണ്ട്. മറ്റൊരു തുടക്ക തീയതി തിരഞ്ഞെടുക്കുക.',
  slotPickDays: 'പരിപാടിയുടെ ആദ്യ ദിവസവും അവസാന ദിവസവും തിരഞ്ഞെടുക്കുക.',
  slotSessionHeading: 'സെഷൻ (കൂടുതൽ സമയത്തിന് അടുത്തടുത്തുള്ള സെഷനുകൾ എടുക്കുക)',
  slotAllSessions: { one: 'എല്ലാ {count} സെഷനും', other: 'എല്ലാ {count} സെഷനുകളും' },
  slotHours: 'മണിക്കൂറുകൾ',
  slotHourCount: { one: '{count} മണിക്കൂർ', other: '{count} മണിക്കൂർ' },
  slotSelected: 'തിരഞ്ഞെടുത്തു',
  slotPickSession: 'വില കാണാൻ ഒരു സെഷനോ ദിവസം മുഴുവനോ തിരഞ്ഞെടുക്കുക.',
  slotPickHours: 'ഒരു മണിക്കൂർ ബുക്ക് ചെയ്യാൻ അതിൽ തൊടുക. കൂടുതൽ സമയത്തിന് രണ്ടാമതൊരു മണിക്കൂർ തൊടുക: ഇടയിലുള്ള എല്ലാ മണിക്കൂറുകളും ചേരും.',
  slotSelectedRange: 'തിരഞ്ഞെടുത്തത് {from} – {to}.',
  slotRequested: 'മറ്റൊരാളും അഭ്യർത്ഥിച്ചിട്ടുണ്ട്',
  slotCrossesTaken: 'തിരഞ്ഞെടുത്ത സമയത്തിനിടയിൽ ഒഴിവില്ലാത്ത ഒരു മണിക്കൂറുണ്ട്. മറ്റൊരു സമയം തിരഞ്ഞെടുക്കുക.',
  slotTurnaround: 'ഇടവേള',
  slotDayPartlyBooked: 'ലഭ്യമല്ല — ദിവസത്തിന്റെ ഒരു ഭാഗം ബുക്ക് ചെയ്തിട്ടുണ്ട്',
  slotDayBlocked: 'ലഭ്യമല്ല — അന്ന് വേദി അടഞ്ഞിരിക്കുന്നു',
  slotBlockedAllDay: 'അന്ന് ദിവസം മുഴുവൻ വേദി അടഞ്ഞിരിക്കുന്നു',
  slotBlockedPartOfDay: 'ഈ ദിവസത്തിന്റെ ഒരു ഭാഗം അടഞ്ഞിരിക്കുന്നു',

  // --------------------------------------------------------- manager console
  mgrDashboard: 'മാനേജർ ഡാഷ്‌ബോർഡ്',
  mgrDashboardSubtitle: 'നിങ്ങളുടെ വേദികൾ, ബുക്കിംഗുകൾ, വരുമാനം.',
  mgrSupervisorDashboard: 'സൂപ്പർവൈസർ ഡാഷ്‌ബോർഡ്',
  mgrSupervisorSubtitle: 'നിങ്ങൾ നോക്കുന്ന വേദികളും അവയുടെ ബുക്കിംഗുകളും.',
  mgrAwaitingApproval: 'അംഗീകാരം കാത്തിരിക്കുന്നു',
  mgrAwaitingApprovalBody:
    'ഹാളുകൾ ചേർക്കുന്നതിന് മുൻപ് ഒരു അഡ്മിൻ നിങ്ങളുടെ മാനേജർ അക്കൗണ്ട് അംഗീകരിക്കണം.',
  mgrTabOverview: 'അവലോകനം',
  mgrTabHalls: 'ഹാളുകൾ',
  mgrTabBookings: 'ബുക്കിംഗുകൾ',
  mgrTabEnquiries: 'അന്വേഷണങ്ങൾ',
  mgrTabTeam: 'സൂപ്പർവൈസർമാർ',
  mgrTabAssets: 'ഉപകരണങ്ങൾ',

  // ------------------------------------------------------------------ assets
  astTitle: 'ഉപകരണങ്ങൾ',
  astSubtitle: 'ഈ വേദിയുടെ ഉപകരണങ്ങളും അവയുടെ അവസ്ഥയും.',
  astPickVenue: 'വേദി',
  astAdd: 'ഇനം ചേർക്കുക',
  astEdit: 'ഇനം തിരുത്തുക',
  astNone: 'ഇതുവരെ ഇനങ്ങളൊന്നും ചേർത്തിട്ടില്ല',
  astNoneBody: 'ഈ വേദിയിലെ ഉപകരണങ്ങൾ — പ്രൊജക്ടർ, കസേരകൾ, സൗണ്ട് സിസ്റ്റം — ഇവിടെ ചേർക്കുക; അവയുടെ അവസ്ഥ ഇവിടെ രേഖപ്പെടുത്താം.',
  astNoneReadOnly: 'ഈ വേദിക്കായി ഇതുവരെ ഒന്നും ചേർത്തിട്ടില്ല.',
  astSummary: {
    one: '{count} ഇനം',
    other: '{count} ഇനങ്ങൾ',
  },
  astNeedsAttention: {
    one: '{count} എണ്ണത്തിന് ശ്രദ്ധ വേണം',
    other: '{count} എണ്ണത്തിന് ശ്രദ്ധ വേണം',
  },
  astAllWell: 'എല്ലാം ശരിയാണ്',
  astColItem: 'ഇനം',
  astColQty: 'എണ്ണം',
  astColStatus: 'അവസ്ഥ',
  astColChecked: 'അവസാനം പരിശോധിച്ചത്',
  astSearch: 'ഇനങ്ങൾ തിരയുക…',
  astFilterAll: 'ഏത് അവസ്ഥയും',
  astShowRetired: 'ഒഴിവാക്കിയവ കാണിക്കുക',
  astName: 'എന്താണ് ഇത്?',
  astNamePlaceholder: 'പ്രൊജക്ടർ, കസേര, സൗണ്ട് സിസ്റ്റം…',
  astQuantity: 'വേദിയിൽ എത്ര എണ്ണം ഉണ്ട്?',
  astAffected: 'എത്ര എണ്ണത്തിന് പ്രശ്നമുണ്ട്?',
  astNote: 'കുറിപ്പ് (നിർബന്ധമല്ല)',
  astNotePlaceholder: 'ബൾബ് മാറ്റി, ഒരെണ്ണം കൂടി സ്റ്റോർ അലമാരയിലുണ്ട്',
  astStatusWorking: 'പ്രവർത്തിക്കുന്നു',
  astStatusMaintenance: 'അറ്റകുറ്റപ്പണിയിൽ',
  astStatusDamaged: 'കേടായത്',
  astStatusRetired: 'ഒഴിവാക്കി',
  astSomeAffected: '{count} എണ്ണം {status}',
  astCheckedOn: '{date}-ന് പരിശോധിച്ചു',
  astMarkAs: 'അവസ്ഥ മാറ്റുക',
  astSaved: 'ഇനം സേവ് ചെയ്തു',
  astRemoved: 'ഇനം നീക്കി',
  astRemoveTitle: 'ഈ ഇനം നീക്കണോ?',
  astRemoveBody: '{venue}-ൽ നിന്ന് {name} നീക്കം ചെയ്യും. അതിന്റെ പഴയ വിവരങ്ങളും പോകും — ഉപയോഗം നിർത്തിയതാണെങ്കിൽ "ഒഴിവാക്കി" എന്ന് മാറ്റിയാൽ രേഖ നിലനിൽക്കും.',
  astRemoveConfirm: 'ഇനം നീക്കുക',
  astAllVenues: 'എല്ലാ വേദികളും',
  astColVenue: 'വേദി',
  astCategory: 'വിഭാഗം',
  astAnyCategory: 'ഏത് വിഭാഗവും',
  astNoCategory: 'നൽകിയിട്ടില്ല',
  astCatFurniture: 'ഫർണിച്ചർ',
  astCatAudioVisual: 'ഓഡിയോ & വിഷ്വൽ',
  astCatLighting: 'ലൈറ്റിംഗ്',
  astCatElectrical: 'ഇലക്ട്രിക്കൽ',
  astCatClimate: 'എ.സി. & ഫാനുകൾ',
  astCatKitchen: 'അടുക്കള & കാറ്ററിംഗ്',
  astCatDecor: 'അലങ്കാരം',
  astCatSafety: 'സുരക്ഷ',
  astCatCleaning: 'വൃത്തിയാക്കൽ',
  astCatOther: 'മറ്റുള്ളവ',
  astLocation: 'വേദിയിൽ എവിടെ (നിർബന്ധമല്ല)',
  astLocationPlaceholder: 'സ്റ്റേജ്, സ്റ്റോർ റൂം, ഡൈനിംഗ് ഹാൾ…',
  astBrand: 'കമ്പനി / മോഡൽ (നിർബന്ധമല്ല)',
  astBrandPlaceholder: 'Epson EB-X51',
  astSerial: 'സീരിയൽ നമ്പർ / അസറ്റ് ടാഗ് (നിർബന്ധമല്ല)',
  astSerialPlaceholder: 'SN-0042',
  astSerialShort: 'S/N',
  astPurchasedOn: 'വാങ്ങിയ തീയതി (നിർബന്ധമല്ല)',
  astWarrantyLabel: 'വാറന്റി തീരുന്ന തീയതി (നിർബന്ധമല്ല)',
  astNotRecorded: 'രേഖപ്പെടുത്തിയിട്ടില്ല',
  astWarrantyUntil: '{date} വരെ വാറന്റി',
  astWarrantyEnded: 'വാറന്റി {date}-ന് അവസാനിച്ചു',
  astPurchaseFuture: 'വാങ്ങിയ തീയതി ഇന്നത്തേക്കാൾ പിന്നീടാകരുത്',
  astWarrantyBeforePurchase: 'വാങ്ങിയ തീയതിക്ക് മുൻപ് വാറന്റി തീരാൻ പാടില്ല',
  astMoveNote: 'ഈ ഇനം {from}-ൽ നിന്ന് മാറ്റും.',
  astReadOnlyHint: 'ഇനങ്ങൾ ചേർക്കാനോ നീക്കാനോ നിങ്ങളുടെ മാനേജർ അനുമതി നൽകിയിട്ടില്ല. എന്നാൽ ഒരു ഇനത്തിന്റെ അവസ്ഥ മാറ്റാൻ കഴിയും.',

  mgrCalendarTitle: 'ബുക്കിംഗ് കലണ്ടർ',
  mgrNoVenuesTitle: 'ഇതുവരെ വേദികളില്ല',
  mgrNoVenuesBody: 'ആദ്യത്തെ വേദി ചേർക്കുക, അതിന്റെ ബുക്കിംഗുകൾ ഇവിടെ കാണാം.',
  mgrNoVenuesSupervisor: 'നിങ്ങളുടെ മാനേജർ ഇതുവരെ ഒരു വേദി ഏൽപ്പിച്ചിട്ടില്ല.',

  mgrRevenueNet: 'നെറ്റ് വരുമാനം (കഴിഞ്ഞ 12 മാസം)',
  mgrRevenue: 'വരുമാനം',
  mgrConfirmed: 'സ്ഥിരീകരിച്ചത്',
  mgrPendingApproval: 'പുതിയ അഭ്യർത്ഥനകൾ',
  mgrReportTitle: 'ബുക്കിംഗ് റിപ്പോർട്ട്',
  mgrReportCardTitle: 'നിങ്ങളുടെ വേദികളിലെ എല്ലാ ബുക്കിംഗുകളും',
  mgrReportCardBody: 'ഓരോ ബുക്കിംഗിനും ഒരു വരി: വേദി, അതിഥിയുടെ പേരും ഫോണും, തീയതിയും സമയവും, നില, പേയ്മെന്റ്, തുക.',
  mgrReportPdf: 'PDF ഡൗൺലോഡ്',
  mgrReportCsv: 'Excel (CSV) ഡൗൺലോഡ്',
  mgrRevenue12: 'വരുമാനം — കഴിഞ്ഞ 12 മാസം',
  mgrRevenue12Subtitle: 'ഓരോ മാസവും കിട്ടിയ തുക, തിരികെ നൽകിയത് കുറച്ച്.',
  mgrRevenueByVenue: 'വേദി തിരിച്ചുള്ള വരുമാനം',
  mgrRevenueByVenueSubtitle: 'ഓരോ വേദിയിലും ലഭിച്ച തുക, റീഫണ്ട് കഴിച്ച്.',
  mgrShare: 'വരുമാനത്തിലെ പങ്ക്',
  mgrPerVenue: 'വേദി തിരിച്ച്',
  mgrPerVenueSubtitle: 'ഓരോ ഹാളിന്റെയും ബുക്കിംഗുകളും നെറ്റ് വരുമാനവും.',
  mgrPopularHours: 'ഏറ്റവും കൂടുതൽ ബുക്ക് ചെയ്ത സമയങ്ങൾ',
  mgrPopularHoursSubtitle: 'സ്ഥിരീകരിച്ചതും പൂർത്തിയായതുമായ ബുക്കിംഗുകളിൽ ഏറ്റവും കൂടുതൽ ബുക്ക് ചെയ്യുന്ന തുടക്ക സമയങ്ങൾ.',
  mgrNoData: 'ഇതുവരെ ബുക്കിംഗുകളില്ല',

  mgrYourVenues: 'നിങ്ങളുടെ വേദികൾ',
  mgrListedCount: { one: '{count} എണ്ണം ലിസ്റ്റ് ചെയ്തു', other: '{count} എണ്ണം ലിസ്റ്റ് ചെയ്തു' },
  mgrAddHall: 'ഹാൾ ചേർക്കുക',
  mgrVerified: 'പരിശോധിച്ചു',
  mgrPendingVerification: 'പരിശോധന കാത്തിരിക്കുന്നു',
  mgrPendingVerificationBody:
    'അഡ്മിൻ നിങ്ങളുടെ രേഖകൾ പരിശോധിക്കുന്നു. പരിശോധന കഴിയുന്നതുവരെ അതിഥികൾക്ക് ഈ വേദി കാണാനാകില്ല.',
  mgrVerificationRejected: 'പരിശോധന നിരസിച്ചു',
  mgrRejected: 'നിരസിച്ചു',
  mgrRejectedBody: 'വേദി തിരുത്തി, ശരിയായ രേഖകൾ വീണ്ടും അപ്‌ലോഡ് ചെയ്ത് സമർപ്പിക്കുക.',
  mgrInactive: 'സജീവമല്ല',
  mgrPerHour: '{price}/മണിക്കൂർ',
  mgrGuestCount: { one: '{count} അതിഥി', other: '{count} അതിഥികൾ' },
  mgrBuffer: '{minutes} മിനിറ്റ് ഇടവേള',
  mgrBookForCustomer: 'ഉപഭോക്താവിനായി ബുക്ക് ചെയ്യുക',
  mgrBlockDates: 'തീയതികൾ ബ്ലോക്ക് ചെയ്യുക',
  mgrNoHallsYet: 'ഇതുവരെ ഹാളുകളില്ല',
  mgrNoHallsYetBody: 'ബുക്കിംഗ് സ്വീകരിക്കാൻ ആദ്യത്തെ വേദി ചേർക്കുക.',
  mgrDeleteHallTitle: 'ഈ ഹാൾ ഇല്ലാതാക്കണോ?',
  mgrDeleteHallBody:
    '{name}-ഉം അതിന്റെ ലിസ്റ്റിംഗും നീക്കം ചെയ്യും. ഇതിനകം എടുത്ത ബുക്കിംഗുകളെ ഇത് ബാധിക്കില്ല.',
  mgrDeleted: 'ഇല്ലാതാക്കി',

  mgrNewBooking: 'പുതിയ ബുക്കിംഗ്',
  mgrGuest: 'അതിഥി',
  mgrWhen: 'എപ്പോൾ',
  mgrAtVenue: 'വേദിയിൽ',
  mgrFilterAnyStatus: 'ഏത് നിലയും',
  mgrFilterFrom: 'മുതൽ',
  mgrFilterTo: 'വരെ',
  mgrFilterAnyDate: 'ഏത് തീയതിയും',
  mgrFilterGuest: 'അതിഥി',
  mgrFilterGuestHint: 'പേര് അല്ലെങ്കിൽ മൊബൈൽ നമ്പർ',
  mgrFilterClear: 'ഫിൽട്ടറുകൾ മായ്ക്കുക ({count})',
  mgrBookedAtVenueTip: 'വേദിയിൽ വച്ച് ബുക്ക് ചെയ്തത്',
  mgrApprove: 'അംഗീകരിക്കുക',
  mgrApproveBooking: 'ബുക്കിംഗ് അംഗീകരിക്കുക',
  mgrReject: 'നിരസിക്കുക',
  mgrRejectBooking: 'ബുക്കിംഗ് നിരസിക്കുക',
  mgrNoBookings: 'ബുക്കിംഗുകളില്ല',
  mgrAwaitingCount: {
    one: '{count} എണ്ണം നിങ്ങൾ സ്ഥിരീകരിക്കാനുണ്ട്',
    other: '{count} എണ്ണം നിങ്ങൾ സ്ഥിരീകരിക്കാനുണ്ട്',
  },
  mgrReceiptTip: 'രസീത് ഡൗൺലോഡ് ചെയ്യുക',
  mgrCancelBookingTitle: 'ഈ ബുക്കിംഗ് റദ്ദാക്കണോ?',
  mgrCancelBookingBody:
    'സ്ലോട്ട് ഒഴിയും, അതിഥിയെ അറിയിക്കും. പണമടച്ചതായി രേഖപ്പെടുത്തിയ തുക മുഴുവനും തിരികെ നൽകിയതായി രേഖപ്പെടുത്തും — ആ തുക നിങ്ങൾ നേരിട്ട് അതിഥിക്ക് തിരികെ നൽകുക; ആഘോഷ പണം കൈകാര്യം ചെയ്യുന്നില്ല.',
  mgrCancelBookingAction: 'ബുക്കിംഗ് റദ്ദാക്കുക',
  mgrDone: 'പൂർത്തിയായി',
  mgrUpdated: 'പുതുക്കി',

  // -------------------------------------------------------------- venue form
  hallAddTitle: 'ഹാൾ ചേർക്കുക',
  hallEditTitle: 'ഹാൾ തിരുത്തുക',
  hallRequiredHintBefore: 'ഈ',
  hallRequiredHintAfter: 'അടയാളമുള്ള കള്ളികൾ നിർബന്ധമാണ്.',
  hallSecBasics: 'അടിസ്ഥാന വിവരങ്ങൾ',
  hallSecLocation: 'സ്ഥലം',
  hallSecCapacity: 'ശേഷിയും സൗകര്യങ്ങളും',
  hallSecPhotos: 'ചിത്രങ്ങൾ',
  hallSec360: '360° കാഴ്ച',
  hallSec360Body:
    'അതിഥികൾക്ക് ചിത്രം വലിച്ചുനീക്കി വേദിയുടെ ചുറ്റും കാണാം.',
  hallSecDocuments: 'നിയമപരമായ രേഖകൾ',
  hallSecDocumentsBody:
    'ഈ വേദി പൊതു ആവശ്യങ്ങൾക്ക് ഉപയോഗിക്കാൻ അനുമതിയുള്ളതാണെന്ന് തെളിയിക്കാൻ കെട്ടിട പൂർത്തീകരണ / ഒക്യുപ്പൻസി സർട്ടിഫിക്കറ്റും പ്രിമൈസസ് / പബ്ലിക് അമ്യൂസ്‌മെന്റ് ലൈസൻസും അപ്‌ലോഡ് ചെയ്യുക. വേദി സൈറ്റിൽ വരുന്നതിന് മുൻപ് ഒരു അഡ്മിൻ ഇവ പരിശോധിക്കും.',
  hallSecPricing: 'വില',
  hallSecAvailability: 'ലഭ്യത',

  hallName: 'പേര്',
  hallCategory: 'വിഭാഗം',
  hallBuilding: 'കെട്ടിടം / ഓഡിറ്റോറിയം സമുച്ചയം',
  hallBuildingNone: 'ഒരു കെട്ടിടത്തിന്റെ ഭാഗമല്ല (ഒറ്റ വേദി)',
  hallBuildingNew: 'പുതിയ കെട്ടിടം',
  hallBuildingNameHint: 'ഉദാ. സ്കൈലൈൻ കൺവെൻഷൻ സെന്റർ',
  hallBuildingHint: 'ഒരേ വിലാസത്തിലുള്ള ഹാളുകൾക്ക് (ഉദാ. പ്രധാന ഹാളും മിനി ഹാളും) ഇത് ഉപയോഗിക്കുക. ഓരോ ഹാളിന്റെ പേജിലും മറ്റ് ഹാളുകൾ അതിഥികൾ കാണും.',
  bldTitle: 'കെട്ടിടങ്ങൾ',
  bldIntro: 'ഒരേ വിലാസത്തിലുള്ള വേദികളെ (ഉദാ. പ്രധാന ഹാളും മിനി ഹാളും) ഒരു കെട്ടിടം ഒന്നിച്ചു ചേർക്കുന്നു. ഓരോ വേദിയിലും കെട്ടിടം തിരഞ്ഞെടുക്കുക.',
  bldNone: 'ഇതുവരെ കെട്ടിടങ്ങളൊന്നുമില്ല.',
  bldAdded: 'കെട്ടിടം ചേർത്തു.',
  bldRenamed: 'കെട്ടിടത്തിന്റെ പേര് മാറ്റി.',
  bldRemoved: 'കെട്ടിടം നീക്കം ചെയ്തു. അതിലെ വേദികൾ ഇനി ഒറ്റയ്ക്ക് കാണിക്കും.',
  bldRemoveTip: 'ഗ്രൂപ്പിംഗ് മാത്രം നീക്കും. വേദികൾ നിലനിൽക്കും.',
  bldHallCount: { one: '{count} വേദി', other: '{count} വേദികൾ' },
  hallContactPhone: 'ബന്ധപ്പെടാനുള്ള ഫോൺ',
  hallDescription: 'വിവരണം',
  hallAddress: 'വിലാസം (തെരുവ്)',
  hallLocality: 'പ്രദേശം',
  hallCity: 'നഗരം',
  hallPincode: 'പിൻകോഡ്',
  hallSeatedCapacity: 'ഇരിപ്പിട ശേഷി',
  hallFloatingCapacity: 'ഫ്ലോട്ടിംഗ് ശേഷി',
  hallAcRooms: 'എ.സി. മുറികൾ',
  hallAirConditioning: 'എയർ കണ്ടീഷനിംഗ്',
  hallAc: 'എ.സി.',
  hallNonAc: 'നോൺ-എ.സി.',
  hallLift: 'ലിഫ്റ്റ്',
  hallAvailable: 'ലഭ്യമാണ്',
  hallNotAvailable: 'ലഭ്യമല്ല',
  hallCarParking: 'കാർ പാർക്കിംഗ്',
  hallBikeParking: 'ബൈക്ക് പാർക്കിംഗ്',
  hallCatering: 'കാറ്ററിംഗ്',
  hallCuisine: 'ഭക്ഷണം',
  hallEstablished: 'തുടങ്ങിയ വർഷം',
  hallSeatingLayout: 'ഇരിപ്പിട ക്രമീകരണം',
  hallPerHour: '₹ / മണിക്കൂർ',
  hallPerHalfDay: '₹ / സെഷൻ',
  hallPerDay: '₹ / ദിവസം',
  hallPeakMultiplier: 'വാരാന്ത്യ നിരക്ക് (സാധാരണ നിരക്കിന്റെ ×)',
  hallBufferMinutes: 'വൃത്തിയാക്കാനുള്ള ഇടവേള (മിനിറ്റ്)',
  hallSessionTimes: 'സെഷൻ സമയങ്ങൾ',
  hallSessionTimesHint:
    'അതിഥികൾ ബുക്ക് ചെയ്യുന്ന രാവിലെ, ഉച്ച, വൈകുന്നേരം സമയങ്ങൾ. ഇവയ്ക്കിടയിൽ കുറഞ്ഞത് വൃത്തിയാക്കാനുള്ള ഇടവേളയുടെ അത്രയും സമയം വിടുക; ഇല്ലെങ്കിൽ ഒരു ബുക്കിംഗ് അടുത്ത സെഷനെ തടയും.',
  hallSessionStart: '{label} തുടക്കം',
  hallSessionEnd: '{label} അവസാനം',
  hallSessionEndsFirst: '{label} അവസാനിക്കുന്ന സമയം തുടങ്ങുന്ന സമയത്തിന് ശേഷമായിരിക്കണം.',
  hallAfternoonBeforeMorning: 'രാവിലത്തെ സെഷൻ തീരുന്നതിന് മുൻപ് ഉച്ചസെഷൻ തുടങ്ങാനാവില്ല.',
  hallEveningBeforeAfternoon: 'ഉച്ചസെഷൻ തീരുന്നതിന് മുൻപ് വൈകുന്നേരത്തെ സെഷൻ തുടങ്ങാനാവില്ല.',
  hallBufferGap:
    '{minutes} മിനിറ്റ് വൃത്തിയാക്കൽ ഇടവേള ഉള്ളതിനാൽ സെഷനുകൾക്കിടയിൽ കുറഞ്ഞത് അത്രയും സമയം വിടുക — ഇല്ലെങ്കിൽ ഒരു സെഷൻ ബുക്ക് ചെയ്താൽ അടുത്തത് ബുക്ക് ചെയ്യാനാവില്ല.',
  hallTo: 'മുതൽ',
  hallMidnight: '{time} (അർദ്ധരാത്രി)',
  hallBookableBy: 'ബുക്ക് ചെയ്യാവുന്നത്',
  hallAllowHourly: 'മണിക്കൂർ കണക്കിൽ ബുക്കിംഗ് അനുവദിക്കുക',
  hallAllowHourlyHint:
    'അതിഥികൾക്ക് എപ്പോഴും സെഷൻ (രാവിലെ, ഉച്ച, വൈകുന്നേരം) ബുക്ക് ചെയ്യാം. മണിക്കൂർ കണക്കിലും ബുക്ക് ചെയ്യാൻ അനുവദിക്കാൻ ഇത് ഓൺ ചെയ്യുക.',
  hallListed: 'ലിസ്റ്റ് ചെയ്തത്',
  hallSave: 'ഹാൾ സേവ് ചെയ്യുക',
  hallSaved: 'സേവ് ചെയ്തു',
  hallSubmitted: 'വേദി അഡ്മിൻ പരിശോധനയ്ക്കായി അയച്ചു.',
  hallFillRequired: 'അടയാളപ്പെടുത്തിയ കള്ളികൾ പരിശോധിച്ച് വീണ്ടും ശ്രമിക്കുക.',
  hallCapacityRule: '1-ഓ അതിൽ കൂടുതലോ ഉള്ള പൂർണ്ണസംഖ്യ വേണം',
  hallNotNegative: 'പൂജ്യത്തിൽ കുറയരുത്',
  hallNumRange: '{min} മുതൽ {max} വരെയുള്ള സംഖ്യ നൽകുക',
  hallPincodeRule: 'പിൻകോഡ് 6 അക്കങ്ങൾ ആയിരിക്കണം',
  hallPriceRange: '₹0 മുതൽ ₹{max} വരെയുള്ള തുക നൽകുക',
  hallPriceMin: 'കുറഞ്ഞത് ₹{min} നൽകുക, ഈ നിരക്ക് ഇല്ലെങ്കിൽ 0 നൽകുക',
  hallHourlyPriceRequired: 'മണിക്കൂർ ബുക്കിംഗ് ഓണാണ് — ₹0-ൽ കൂടുതലുള്ള മണിക്കൂർ നിരക്ക് നൽകുക',
  hallPriceRequired: 'കുറഞ്ഞത് ഒരു നിരക്കെങ്കിലും ₹0-ൽ കൂടുതൽ നൽകുക',
  hallSessionPriceRequired: 'മണിക്കൂർ ബുക്കിംഗ് ഓഫാണ് — ₹0-ൽ കൂടുതലുള്ള സെഷൻ / മുഴുവൻ ദിവസ നിരക്ക് നൽകുക',
  hallDocOccupancyHint: 'നിങ്ങളുടെ തദ്ദേശ നഗരസഭ നൽകുന്നത്',
  hallDocLicenseHint: 'നഗരസഭയോ പോലീസ് കമ്മീഷണറേറ്റോ നൽകുന്നത്',
  hallDocUploadRequired: 'ഈ രേഖ അപ്‌ലോഡ് ചെയ്യുക',

  // government ID types offered at manager signup
  idAadhaar: 'ആധാർ',
  idPan: 'പാൻ കാർഡ്',
  idPassport: 'പാസ്‌പോർട്ട്',
  idDrivingLicence: 'ഡ്രൈവിംഗ് ലൈസൻസ്',
  idVoterId: 'വോട്ടർ ഐഡി',
  hallDocRefRequired: 'സർട്ടിഫിക്കറ്റ് / ലൈസൻസ് നമ്പർ നൽകുക',
  hallDocAuthorityRequired: 'രേഖ നൽകിയ ഓഫീസ് / അധികാരിയുടെ പേര് നൽകുക',

  // seating layouts — the value stored stays English; only the label follows
  layoutNotSpecified: 'വ്യക്തമാക്കിയിട്ടില്ല',
  layoutFlexible: 'ആവശ്യാനുസരണം / ഹാൾ മുഴുവൻ',
  layoutTheatre: 'തിയേറ്റർ രീതി',
  layoutBanquet: 'വട്ടമേശകൾ (ബാങ്ക്വറ്റ്)',
  layoutClassroom: 'ക്ലാസ് മുറി രീതി',
  layoutUShape: 'U ആകൃതി',
  layoutTiered: 'പടിപടിയായുള്ള സ്ഥിരം ഇരിപ്പിടം',
  layoutMultiple: 'ഒന്നിലധികം ഹാളുകൾ',

  // catering + cuisine, shared with the public venue page
  cateringNone: 'ലഭ്യമല്ല',
  cateringInhouse: 'വേദിയുടേത് മാത്രം',
  cateringOutside: 'പുറത്തുനിന്നുള്ളതും അനുവദിക്കും',
  cateringBoth: 'വേദിയുടേതും പുറത്തുനിന്നുള്ളതും',
  cuisineVeg: 'വെജ്',
  cuisineNonveg: 'നോൺ-വെജ്',
  cuisineBoth: 'വെജ് & നോൺ-വെജ്',

  // ---------------------------------------------------------- venue booking
  vbNoVenues: 'ആദ്യം ഒരു വേദി ചേർക്കുക — ഇപ്പോൾ ബുക്ക് ചെയ്യാൻ ഒന്നുമില്ല.',
  vbNoneBookable:
    'നിങ്ങളുടെ ഒരു വേദിക്കും ഇതുവരെ ബുക്കിംഗ് സ്വീകരിക്കാനാവില്ല. ബുക്ക് ചെയ്യാൻ വേദി അഡ്മിൻ പരിശോധിച്ച് ലിസ്റ്റ് ചെയ്തിരിക്കണം.',
  vbIntro:
    'നേരിട്ടോ ഫോണിലൂടെയോ ബുക്ക് ചെയ്യുന്ന ഉപഭോക്താവിന്. ഇത് ഉടൻ സ്ഥിരീകരിക്കും; ആ സമയം പിന്നെ ഓൺലൈനിൽ ബുക്ക് ചെയ്യാനാവില്ല.',
  vbHidden: {
    one: 'നിങ്ങളുടെ {total} വേദികളിൽ {hidden} എണ്ണം കാണിക്കുന്നില്ല — പരിശോധിച്ച്, ലിസ്റ്റ് ചെയ്ത വേദികൾ മാത്രമേ ബുക്ക് ചെയ്യാനാകൂ.',
    other:
      'നിങ്ങളുടെ {total} വേദികളിൽ {hidden} എണ്ണം കാണിക്കുന്നില്ല — പരിശോധിച്ച്, ലിസ്റ്റ് ചെയ്ത വേദികൾ മാത്രമേ ബുക്ക് ചെയ്യാനാകൂ.',
  },
  vbCustomerName: 'ഉപഭോക്താവിന്റെ പേര്',
  vbCustomerMobile: 'ഉപഭോക്താവിന്റെ മൊബൈൽ',
  vbCustomerMobileHint: 'അവർക്ക് ഇതിനകം അക്കൗണ്ട് ഉണ്ടെങ്കിൽ ഈ ബുക്കിംഗ് അതിൽ കാണാം.',
  vbCustomerEmail: 'ഉപഭോക്താവിന്റെ ഇമെയിൽ',
  vbNote: 'കുറിപ്പ്',
  vbNoteHint: 'ഉദാ. പിറന്നാൾ, 120 അതിഥികൾ',
  vbPaymentCollected: 'വേദിയിൽ വച്ച് പണം വാങ്ങി',
  vbBooking: 'ബുക്ക് ചെയ്യുന്നു…',
  vbBookMarkPaid: 'ബുക്ക് ചെയ്യുക (പണം അടച്ചു)',
  vbBookUnpaid: 'ബുക്ക് ചെയ്യുക (പണം അടച്ചിട്ടില്ല)',
  vbBookedClaimable: '{name}-നായി ബുക്ക് ചെയ്തു. {phone} ഉപയോഗിച്ച് രജിസ്റ്റർ ചെയ്യുമ്പോൾ അവർക്കിത് കാണാം.',
  vbBookedExisting: '{name}-നായി ബുക്ക് ചെയ്തു — ഇപ്പോൾ അവരുടെ അക്കൗണ്ടിലുണ്ട്.',

  // ------------------------------------------------------------- supervisors
  supSubtitle:
    'നിങ്ങൾ ഏൽപ്പിക്കുന്ന ഹാളുകൾ നോക്കിനടത്തുന്ന ജീവനക്കാർ — ഇവർക്ക് വരുമാനം കാണാനോ വേദികൾ ചേർക്കാനോ ഇല്ലാതാക്കാനോ കഴിയില്ല.',
  supAdd: 'സൂപ്പർവൈസറെ ചേർക്കുക',
  supEditTitle: '{name} എഡിറ്റ് ചെയ്യുക',
  supNewPassword: 'പുതിയ പാസ്‌വേഡ്',
  supNewPasswordHint: 'നിലവിലെ പാസ്‌വേഡ് നിലനിർത്താൻ ശൂന്യമായി വിടുക',
  supPhoneRecheckNote: 'അടുത്ത സൈൻ-ഇന്നിൽ അവർ SMS കോഡ് വഴി പുതിയ നമ്പർ സ്ഥിരീകരിക്കും.',
  supUpdated: 'സൂപ്പർവൈസറെ പുതുക്കി.',
  supUpdatedNewPhone: 'സൂപ്പർവൈസറെ പുതുക്കി. അടുത്ത സൈൻ-ഇന്നിൽ അവർ പുതിയ നമ്പർ സ്ഥിരീകരിക്കും.',
  supCreatedFirstLogin: 'ആദ്യ സൈൻ-ഇന്നിൽ, ഈ നമ്പറിലേക്ക് SMS വഴി അയയ്ക്കുന്ന കോഡ് ഉപയോഗിച്ച് അവർ നമ്പർ സ്ഥിരീകരിക്കും.',
  supMobile: 'മൊബൈൽ',
  supCanEdit: 'തിരുത്താൻ കഴിയും',
  supAdded: 'ചേർത്ത തീയതി',
  supNoneAssigned: 'ഒന്നും ഏൽപ്പിച്ചിട്ടില്ല',
  supNone: 'ഇതുവരെ സൂപ്പർവൈസർമാരില്ല — ഒരാളെ ചേർത്ത് ഒരു ഹാൾ ഏൽപ്പിക്കുക.',
  supNoVenues: 'ഏൽപ്പിക്കാൻ ഇതുവരെ വേദികളില്ല.',
  supDelisted: 'ലിസ്റ്റിൽ നിന്ന് നീക്കി',
  supRemoved: 'സൂപ്പർവൈസറെ നീക്കം ചെയ്തു',
  supRemoveTitle: 'ഈ സൂപ്പർവൈസറെ നീക്കം ചെയ്യണോ?',
  supRemoveBody: '{name}-ന് ഇനി സൈൻ ഇൻ ചെയ്യാൻ കഴിയില്ല.',
  supCanEditOn: '{name}-ന് ഇനി അവരുടെ വേദികൾ തിരുത്താം',
  supCanEditOff: '{name}-ന് ഇനി വേദികൾ തിരുത്താനാവില്ല',
  supCanEditAria: '{name}-ന് വേദി വിവരങ്ങൾ തിരുത്താൻ അനുവദിക്കുക',
  supCanEditLabel: 'വേദി വിവരങ്ങൾ തിരുത്താം',
  supCanEditHint:
    'വേദിയുടെ വിവരങ്ങൾ, വില, ചിത്രങ്ങൾ, രേഖകൾ എന്നിവ മാറ്റാൻ ഇത് അനുവദിക്കുന്നു. ഏൽപ്പിച്ച ഹാളിൽ തീയതി ബ്ലോക്ക് ചെയ്യാനും ബുക്കിംഗ് എടുക്കാനും അവർക്ക് എപ്പോഴും കഴിയും.',
  supAssignIntro: 'ഇവിടെ ടിക്ക് ചെയ്യുന്ന ഹാളുകൾ മാത്രമേ {name} കാണുകയും നോക്കിനടത്തുകയും ചെയ്യൂ.',
  supAssign: 'ഹാൾ ഏൽപ്പിക്കുക',
  supAssigning: 'ഏൽപ്പിക്കുന്നു…',
  supHallsUpdated: 'ഹാളുകൾ പുതുക്കി',
  supHallsLabel: 'അവർ നോക്കുന്ന ഹാളുകൾ',
  supPermissions: 'അനുമതികൾ',
  supFullName: 'പൂർണ്ണ പേര്',
  supPasswordHint: 'ചുരുങ്ങിയത് 6 അക്ഷരങ്ങൾ വേണം',
  supPasswordNote: 'ഒരു പാസ്‌വേഡ് തീരുമാനിച്ച് അവർക്ക് നൽകുക.',
  supCreate: 'സൂപ്പർവൈസർ അക്കൗണ്ട് ഉണ്ടാക്കുക',
  supCreating: 'ഉണ്ടാക്കുന്നു…',
  supCreatedTitle: 'സൂപ്പർവൈസർ അക്കൗണ്ട് ഉണ്ടാക്കി',
  supCreatedBody: 'ഈ സൈൻ ഇൻ വിവരങ്ങൾ {name}-ന് നൽകുക. പാസ്‌വേഡ് വീണ്ടും കാണിക്കില്ല.',
  supCreatedAssigned:
    'അവർ സാധാരണ ലോഗിൻ പേജിൽ സൈൻ ഇൻ ചെയ്യണം; നിങ്ങൾ ഏൽപ്പിച്ച വേദികൾ മാത്രമേ അവർക്ക് കാണാനാകൂ.',
  supCreatedUnassigned: 'ഇതുവരെ ഹാളുകൾ ഏൽപ്പിച്ചിട്ടില്ല — ഏൽപ്പിക്കുന്നതുവരെ അവർ ഒന്നും കാണില്ല.',

  // --------------------------------------------------------------- uploaders
  upDragPhotos: 'ചിത്രങ്ങൾ ഇവിടെ വലിച്ചിടുക',
  upOrBrowse: 'അല്ലെങ്കിൽ ക്ലിക്ക് ചെയ്ത് തിരഞ്ഞെടുക്കുക',
  upPano: 'ഒരു 360° പനോരമ അപ്‌ലോഡ് ചെയ്യുക',
  upMakeCover: 'കവർ ചിത്രമാക്കുക',
  upRemovePhoto: 'ചിത്രം നീക്കം ചെയ്യുക',
  upTooManyPhotos: 'ഒരു വേദിക്ക് പരമാവധി {max} ചിത്രങ്ങൾ മാത്രം',
  upOnlyFirst: {
    one: 'ആദ്യത്തെ ചിത്രം മാത്രം ചേർത്തു',
    other: 'ആദ്യത്തെ {count} ചിത്രങ്ങൾ മാത്രം ചേർത്തു',
  },
  upReplace: 'മാറ്റുക',
  upUploadFile: 'ഫയൽ അപ്‌ലോഡ് ചെയ്യുക',
  upCover: 'കവർ',
  upNoFileYet: 'ഇതുവരെ ഫയൽ അപ്‌ലോഡ് ചെയ്തിട്ടില്ല',
  upViewFile: 'ഫയൽ കാണുക',
  upCertNumber: 'സർട്ടിഫിക്കറ്റ് / ലൈസൻസ് നമ്പർ',
  upIssuingAuthority: 'നൽകിയ അധികാരി',

  // ------------------------------------------------------------------ charts
  chartNoMonthly: 'കഴിഞ്ഞ 12 മാസത്തിൽ വരുമാനമില്ല.',
  chartNoVenue: 'ഇതുവരെ വേദി വരുമാനമില്ല.',
  chartMonthlyAria: 'മാസം തിരിച്ചുള്ള നെറ്റ് വരുമാനം',
  chartVenueAria: 'വേദി തിരിച്ചുള്ള നെറ്റ് വരുമാനം',

  // -------------------------------------------------------------- block dates
  blkFrom: 'മുതൽ',
  blkTo: 'വരെ',
  blkReason: 'കാരണം',
  blkReasonHint: 'അറ്റകുറ്റപ്പണി / അവധി / സ്വകാര്യ പരിപാടി',
  blkAdd: 'ബ്ലോക്ക് ചേർക്കുക',
  blkBlocked: 'ബ്ലോക്ക് ചെയ്തു',
  blkInvalidRange: 'തുടക്കവും അവസാനവും തിരഞ്ഞെടുക്കുക; അവസാനം തുടക്കത്തിന് ശേഷമായിരിക്കണം.',
  blkExisting: 'നിലവിലുള്ള ബ്ലോക്കുകൾ',
  blkNone: 'ഒന്നുമില്ല',
  blkRemove: 'ബ്ലോക്ക് നീക്കം ചെയ്യുക',
  blkBlockedRange: '{range} ബ്ലോക്ക് ചെയ്തു. ഈ സമയം ആർക്കും ബുക്ക് ചെയ്യാനാവില്ല.',
  mgrToastApproved: 'ബുക്കിംഗ് #{id} സ്ഥിരീകരിച്ചു. അതിഥിയെ അറിയിച്ചു.',
  mgrToastDeclined: 'അഭ്യർത്ഥന #{id} നിരസിച്ചു. അതിഥിയെ അറിയിച്ചു.',
  mgrToastCancelled: 'ബുക്കിംഗ് #{id} റദ്ദാക്കി. അതിഥിയെ അറിയിച്ചു.',
  mgrToastVenueDeleted: '{name} ഇല്ലാതാക്കി.',
  mgrToastEnquiryContacted: 'അന്വേഷണം ബന്ധപ്പെട്ടതായി അടയാളപ്പെടുത്തി.',
  mgrToastEnquiryClosed: 'അന്വേഷണം അവസാനിപ്പിച്ചു.',
  hallSavedNamed: '{name}-ലെ മാറ്റങ്ങൾ സേവ് ചെയ്തു.',
  blkOverlap: 'ഈ സമയം ഇതിനകം ബ്ലോക്ക് ചെയ്തിട്ടുണ്ട്. താഴെയുള്ള ബ്ലോക്കുകൾക്ക് പുറത്തുള്ള സമയം തിരഞ്ഞെടുക്കുക.',
  blkRemoveTitle: 'ഈ ബ്ലോക്ക് നീക്കണോ?',
  blkRemoveBody: '{range} വീണ്ടും ബുക്കിംഗിനായി തുറക്കും.',
  blkRemoveAction: 'ബ്ലോക്ക് നീക്കം ചെയ്യുക',
  blkBookingsOn: '{date}-ലെ ബുക്കിംഗുകൾ',
  blkNothingBooked: 'ഒന്നും ബുക്ക് ചെയ്തിട്ടില്ല — ദിവസം മുഴുവൻ ബ്ലോക്ക് ചെയ്യാം.',
  blkInsideWindow: 'നിങ്ങൾ ബ്ലോക്ക് ചെയ്യുന്ന സമയത്തിനുള്ളിൽ',
  blkClashWarning: {
    one: 'ഈ സമയം ബ്ലോക്ക് ചെയ്താൽ ആ ബുക്കിംഗ് റദ്ദാകില്ല — പുതിയവ മാത്രമേ തടയൂ. വേദി ശരിക്കും ലഭ്യമല്ലെങ്കിൽ ആദ്യം അത് റദ്ദാക്കുക.',
    other:
      'ഈ സമയം ബ്ലോക്ക് ചെയ്താൽ ആ ബുക്കിംഗുകൾ റദ്ദാകില്ല — പുതിയവ മാത്രമേ തടയൂ. വേദി ശരിക്കും ലഭ്യമല്ലെങ്കിൽ ആദ്യം അവ റദ്ദാക്കുക.',
  },

  // ------------------------------------------------------------------ enquiries
  enqFrom: 'ആരിൽ നിന്ന്',
  enqEvent: 'പരിപാടി',
  enqMessage: 'സന്ദേശം',
  enqMarkContacted: 'ബന്ധപ്പെട്ടു എന്ന് അടയാളപ്പെടുത്തുക',
  enqNone: 'ഇതുവരെ അന്വേഷണങ്ങളില്ല',

  // ----------------------------------------------------------- admin console
  admNavManagers: 'മാനേജർമാർ',
  admNavUsers: 'ഉപയോക്താക്കൾ',
  admNavMessages: 'സന്ദേശങ്ങൾ',
  admNavAudit: 'ഓഡിറ്റ് ലോഗ്',
  mgrTabSubscription: 'സബ്‌സ്ക്രിപ്ഷൻ',
  admNavSubscriptions: 'സബ്‌സ്ക്രിപ്ഷനുകൾ',
  subBannerExpired: 'നിങ്ങളുടെ സബ്‌സ്ക്രിപ്ഷൻ അവസാനിച്ചു, അതിനാൽ വേദികൾ അതിഥികൾക്ക് കാണില്ല. ബുക്കിംഗുകൾ സുരക്ഷിതമാണ്. വീണ്ടും ലിസ്റ്റ് ചെയ്യാൻ പുതുക്കുക.',
  subBannerTrial: 'നിങ്ങളുടെ സൗജന്യ മാസം {count} ദിവസത്തിനുള്ളിൽ ({date}) അവസാനിക്കും. വേദികൾ ലിസ്റ്റിൽ നിലനിർത്താൻ ഒരു പ്ലാൻ തിരഞ്ഞെടുക്കുക.',
  subBannerEnding: 'നിങ്ങളുടെ സബ്‌സ്ക്രിപ്ഷൻ {count} ദിവസത്തിനുള്ളിൽ ({date}) അവസാനിക്കും. വേദികൾ ലിസ്റ്റിൽ നിലനിർത്താൻ പുതുക്കുക.',
  subRenew: 'പുതുക്കുക',
  subSeePlans: 'പ്ലാനുകൾ കാണുക',
  subStateExpired: 'കാലാവധി കഴിഞ്ഞു',
  subStateTrial: 'സൗജന്യ മാസം',
  subStateActive: '{plan} പ്ലാൻ',
  subStateNone: 'ആരംഭിച്ചിട്ടില്ല',
  subEndedOn: '{date}-ന് അവസാനിച്ചു',
  subListedUntil: '{date} വരെ വേദികൾ ലിസ്റ്റിൽ',
  subDaysLeft: '{count} ദിവസം ബാക്കി',
  subExpiredBody: 'നിങ്ങളുടെ വേദികൾ അതിഥികൾക്ക് കാണില്ല, പുതിയ അഭ്യർത്ഥനകളും വരില്ല. ബുക്കിംഗുകൾ കാണാനും കൈകാര്യം ചെയ്യാനും കഴിയും. വീണ്ടും ലിസ്റ്റ് ചെയ്യാൻ താഴെയുള്ള ഒരു പ്ലാനിന് പണമടയ്ക്കുക.',
  subRenewEarlyBody: 'നേരത്തെ പുതുക്കിയാൽ ഒന്നും നഷ്ടപ്പെടില്ല: പുതിയ പ്ലാൻ നിലവിലുള്ളത് അവസാനിക്കുമ്പോൾ തുടങ്ങും.',
  subPlans: 'പ്ലാനുകൾ',
  subPerMonth: 'പ്രതിമാസം',
  subForMonths: '{count} മാസത്തേക്ക്',
  subMonthly: 'മാസം {price}',
  subHowToPay: 'എങ്ങനെ പണമടയ്ക്കാം',
  subAfterPaying: 'പണമടച്ച ശേഷം ഇടപാട് ഐഡിയും രജിസ്റ്റർ ചെയ്ത ഫോൺ നമ്പറുമായി ഞങ്ങളെ വിളിക്കുകയോ ഇമെയിൽ ചെയ്യുകയോ ചെയ്യുക. ഞങ്ങൾ പണമടവ് രേഖപ്പെടുത്തും, പ്ലാൻ ഉടൻ തുടങ്ങും.',
  subPayUpi: 'UPI വഴി അടയ്ക്കുക',
  subPayBank: 'ബാങ്ക് ട്രാൻസ്ഫർ വഴി അടയ്ക്കുക',
  subUpiId: 'UPI ഐഡി',
  subPayee: 'സ്വീകർത്താവ്',
  subPayWithUpiApp: 'UPI ആപ്പിൽ അടയ്ക്കുക',
  subAccountName: 'അക്കൗണ്ട് പേര്',
  subAccountNumber: 'അക്കൗണ്ട് നമ്പർ',
  subIfsc: 'IFSC',
  subBank: 'ബാങ്ക്',
  subBankName: 'ബാങ്കിന്റെ പേര്',
  subBranch: 'ബ്രാഞ്ച്',
  subCopy: 'കോപ്പി',
  subCopyLabel: '{label} കോപ്പി ചെയ്യുക',
  subCopied: '{label} കോപ്പി ചെയ്തു.',
  subCopyFailed: 'കോപ്പി ചെയ്യാനായില്ല. തിരഞ്ഞെടുത്ത് കോപ്പി ചെയ്യുക.',
  subPaymentDetails: 'പണമടയ്ക്കൽ വിവരങ്ങൾ',
  subPaymentIntro: 'മാനേജർമാരുടെ സബ്‌സ്ക്രിപ്ഷൻ ടാബിൽ "എങ്ങനെ പണമടയ്ക്കാം" എന്നതിന് കീഴിൽ കാണിക്കും. UPI, ബാങ്ക് ട്രാൻസ്ഫർ, അല്ലെങ്കിൽ രണ്ടും നൽകുക.',
  subPaymentNote: 'മാനേജർമാർക്കുള്ള കുറിപ്പ്',
  subPaymentNoteHint: 'ഉദാ: പണമടവ് കുറിപ്പിൽ ഫോൺ നമ്പർ ചേർക്കുക.',
  subSavePayment: 'പണമടയ്ക്കൽ വിവരങ്ങൾ സേവ് ചെയ്യുക',
  subPaymentSaved: 'പണമടയ്ക്കൽ വിവരങ്ങൾ സേവ് ചെയ്തു. മാനേജർമാർക്ക് ഇപ്പോൾ കാണാം.',
  subHowToPayBody: 'UPI, ബാങ്ക് ട്രാൻസ്ഫർ അല്ലെങ്കിൽ പണമായി Aghosha-യ്ക്ക് അടച്ച ശേഷം രജിസ്റ്റർ ചെയ്ത ഫോൺ നമ്പറുമായി ഞങ്ങളെ വിളിക്കുകയോ ഇമെയിൽ ചെയ്യുകയോ ചെയ്യുക. ഞങ്ങൾ പണമടവ് രേഖപ്പെടുത്തും, പ്ലാൻ ഉടൻ തുടങ്ങും.',
  subHistory: 'ചരിത്രം',
  subFreeMonth: 'സൗജന്യ മാസം',
  subFree: 'സൗജന്യം',
  subPillActive: 'പണമടച്ചത്',
  subPillEnding: '7 ദിവസത്തിനുള്ളിൽ അവസാനിക്കും',
  subAdminIntro: 'മാനേജർമാർക്ക് ആദ്യ മാസം സൗജന്യം, പിന്നീട് പണമടച്ച പ്ലാൻ. നിങ്ങൾ രേഖപ്പെടുത്തുന്ന പണമടവ് മാനേജറുടെ നിലവിലെ കാലാവധി കഴിയുമ്പോൾ തുടങ്ങും. കാലാവധി കഴിഞ്ഞാൽ പുതുക്കുന്നതുവരെ വേദികൾ അതിഥികൾക്ക് കാണില്ല.',
  subManagers: 'മാനേജർമാർ',
  subSearch: 'പേര്, ഫോൺ അല്ലെങ്കിൽ ഇമെയിൽ',
  subAllManagers: 'എല്ലാ മാനേജർമാരും ({count})',
  subNoManagers: 'യോജിക്കുന്ന മാനേജർമാരില്ല.',
  subVenues: '{count} വേദികൾ',
  subUntilDays: '{date} വരെ · {count} ദിവസം ബാക്കി',
  subNotStarted: 'അക്കൗണ്ട് അംഗീകരിക്കുമ്പോൾ തുടങ്ങും',
  subRecordPayment: 'പണമടവ് രേഖപ്പെടുത്തുക',
  subRecordPaymentFor: '{name}-ൽ നിന്നുള്ള പണമടവ് രേഖപ്പെടുത്തുക',
  subPlanSaved: 'പ്ലാൻ സേവ് ചെയ്തു.',
  subPlanHidden: 'പ്ലാൻ മാനേജർമാരിൽ നിന്ന് മറച്ചു.',
  subPlanShown: 'പ്ലാൻ വീണ്ടും മാനേജർമാർക്ക് ലഭ്യമാക്കി.',
  subPrice: 'വില (₹)',
  subMonthsLabel: 'മാസങ്ങൾ',
  subOneMonth: '1 മാസം',
  subPlanOff: 'മറച്ചത്',
  subHidePlan: 'മറയ്ക്കുക',
  subShowPlan: 'വീണ്ടും ലഭ്യമാക്കുക',
  subRecorded: 'പണമടവ് രേഖപ്പെടുത്തി. {name} {date} വരെ ലിസ്റ്റിൽ.',
  subPlan: 'പ്ലാൻ',
  subAmountReceived: 'ലഭിച്ച തുക (₹)',
  subMethod: 'പണമടച്ച രീതി',
  subMethodUpi: 'UPI',
  subMethodBank: 'ബാങ്ക് ട്രാൻസ്ഫർ',
  subMethodCash: 'പണം',
  subMethodCheque: 'ചെക്ക്',
  subMethodOther: 'മറ്റുള്ളവ',
  subReference: 'റഫറൻസ്',
  subReferenceHint: 'UPI ഇടപാട് ഐഡി, ചെക്ക് നമ്പർ…',
  subNote: 'കുറിപ്പ്',
  subWillRun: 'ഈ പ്ലാൻ {from} മുതൽ {to} വരെ.',
  subCancelled: 'പണമടവ് റദ്ദാക്കി.',
  subHistoryFor: '{name}: സബ്‌സ്ക്രിപ്ഷൻ ചരിത്രം',
  subNoHistory: 'ഇതുവരെ ഒന്നും രേഖപ്പെടുത്തിയിട്ടില്ല.',
  subCancelPayment: 'പണമടവ് റദ്ദാക്കുക',
  subCancelBody: 'തെറ്റായി രേഖപ്പെടുത്തിയ പണമടവിന് മാത്രം. ആ കാലയളവ് നീക്കം ചെയ്യും, മാനേജറുടെ അവസാന തീയതി ശേഷിക്കുന്ന കാലയളവിലേക്ക് മാറും.',
  admNavMasterData: 'മാസ്റ്റർ ഡാറ്റ',
  mdIntro: 'വേദി, ബുക്കിംഗ്, ഉപകരണ ഫോമുകളിൽ കാണിക്കുന്ന ഓപ്ഷനുകൾ. ഒരു ഇനം നീക്കം ചെയ്താൽ അത് ഫോമുകളിലും ഫിൽട്ടറുകളിലും കാണില്ല; ഇതിനകം ഉപയോഗിക്കുന്നവയിൽ അത് നിലനിൽക്കും, പിന്നീട് തിരികെ ചേർക്കാം.',
  mdVenueTypes: 'വേദി തരങ്ങൾ',
  mdVenueTypesHint: 'മാനേജർ വേദിക്ക് തിരഞ്ഞെടുക്കുന്നതും അതിഥികൾ ഫിൽട്ടർ ചെയ്യുന്നതുമായ വിഭാഗങ്ങൾ.',
  mdEventTypes: 'പരിപാടി തരങ്ങൾ',
  mdEventTypesHint: 'ബുക്കിംഗ് ആവശ്യപ്പെടുമ്പോൾ അതിഥി തിരഞ്ഞെടുക്കുന്ന പരിപാടികൾ. ഗൈഡ് പേജുകൾ നിലവിലുള്ള തരങ്ങൾക്ക് മാത്രം.',
  mdSeatingLayouts: 'ഇരിപ്പിട ക്രമീകരണങ്ങൾ',
  mdSeatingLayoutsHint: 'മാനേജർ വേദി ഫോമിൽ തിരഞ്ഞെടുക്കുന്ന ക്രമീകരണങ്ങൾ. വേദി പേജിൽ കാണിക്കും.',
  mdAmenities: 'സൗകര്യങ്ങൾ',
  mdAmenitiesHint: 'വേദിയിലുള്ള സൗകര്യങ്ങൾ. മാനേജർമാർ വേദി ഫോമിൽ ഇവ ടിക്ക് ചെയ്യുന്നു.',
  mdAssetCategories: 'ഉപകരണ വിഭാഗങ്ങൾ',
  mdAssetCategoriesHint: 'മാനേജർമാർ ഉപകരണങ്ങളെ തരംതിരിക്കുന്ന വിഭാഗങ്ങൾ.',
  mdUsedVenues: '{count} വേദികൾ ഉപയോഗിക്കുന്നു',
  mdUsedBookings: '{count} ബുക്കിംഗുകളിൽ',
  mdUsedAssets: '{count} ഉപകരണങ്ങളിൽ',
  mdNameEn: 'പേര് (ഇംഗ്ലീഷ്)',
  mdNameMl: 'പേര് (മലയാളം)',
  mdIcon: 'ഐക്കൺ (Remix ഐക്കൺ പേര്)',
  mdNoMalayalam: 'മലയാളം പേരില്ല: ഇംഗ്ലീഷ് കാണിക്കും',
  mdNameRequired: 'ഇംഗ്ലീഷ് പേര് നൽകുക.',
  mdAdd: 'ചേർക്കുക',
  mdAdded: 'ഫോമുകളിലും ഫിൽട്ടറുകളിലും ചേർത്തു.',
  mdEdit: 'തിരുത്തുക',
  mdSaved: 'സേവ് ചെയ്തു.',
  mdRemove: 'നീക്കം ചെയ്യുക',
  mdRemoved: 'ഫോമുകളിൽ നിന്നും ഫിൽട്ടറുകളിൽ നിന്നും നീക്കി. നിലവിലുള്ള രേഖകളിൽ അത് നിലനിൽക്കും.',
  mdRestore: 'തിരികെ ചേർക്കുക',
  mdRestored: 'ഫോമുകളിലും ഫിൽട്ടറുകളിലും തിരികെ ചേർത്തു.',
  mdRemovedTitle: 'നീക്കം ചെയ്തവ ({count})',
  admPlatformAdmin: 'പ്ലാറ്റ്‌ഫോം അഡ്മിൻ',

  // admin sign-in
  admSignIn: 'സൈൻ ഇൻ',
  admSigningIn: 'സൈൻ ഇൻ ചെയ്യുന്നു…',
  admSignInConsole: 'കൺസോളിലേക്ക് സൈൻ ഇൻ ചെയ്യുക',
  admAccessOnly: 'അഡ്മിൻമാർക്ക് മാത്രം.',
  admWrongAccount: 'തെറ്റായ അക്കൗണ്ട്',
  admSignedInAs: 'നിങ്ങൾ സൈൻ ഇൻ ചെയ്തിരിക്കുന്നത്',
  admNeedsAdmin: 'അഡ്മിൻ കൺസോളിന് ഒരു അഡ്മിൻ അക്കൗണ്ട് വേണം.',
  admSignOutSwitch: 'സൈൻ ഔട്ട് ചെയ്ത് അഡ്മിൻ അക്കൗണ്ട് ഉപയോഗിക്കുക',
  admBackToSite: '{name}-ലേക്ക് മടങ്ങുക',

  // overview
  admBookers: 'ബുക്ക് ചെയ്യുന്നവർ',
  admGmv: 'ആകെ വിൽപ്പന',
  admInbox: 'ഇൻബോക്സ്',
  admSuspendedCount: { one: '{count} നിർത്തിവച്ചത്', other: '{count} നിർത്തിവച്ചവ' },
  admAwaitingApprovalCount: {
    one: '{count} അംഗീകാരം കാത്തിരിക്കുന്നു',
    other: '{count} അംഗീകാരം കാത്തിരിക്കുന്നു',
  },
  admAwaitingDecisionCount: {
    one: '{count} തീരുമാനം കാത്തിരിക്കുന്നു',
    other: '{count} തീരുമാനം കാത്തിരിക്കുന്നു',
  },
  admLiveCount: { one: '{count} സജീവം', other: '{count} സജീവം' },
  admRefunded: '{amount} തിരികെ നൽകി',
  admMessageCount: { one: '{count} സന്ദേശം', other: '{count} സന്ദേശങ്ങൾ' },
  admEnquiryCount: { one: '{count} അന്വേഷണം', other: '{count} അന്വേഷണങ്ങൾ' },
  admNewCount: { one: '{count} പുതിയത്', other: '{count} പുതിയവ' },
  admUnhandledCount: { one: '{count} കൈകാര്യം ചെയ്തിട്ടില്ല', other: '{count} കൈകാര്യം ചെയ്തിട്ടില്ല' },
  admActionCount: { one: 'അവസാന {count} നടപടി', other: 'അവസാന {count} നടപടികൾ' },
  admTopVenues: 'വരുമാനത്തിൽ മുന്നിലുള്ള വേദികൾ',
  admNoVenues: 'ഇതുവരെ വേദികളില്ല.',

  // shared table bits
  admContact: 'ബന്ധപ്പെടാൻ',
  admAccount: 'അക്കൗണ്ട്',
  admAccountNumber: 'അക്കൗണ്ട് #{id}',
  admIdNumber: 'ഐഡി #{id}',
  admNoEmail: 'ഇമെയിൽ ഇല്ല',
  admNetSpend: 'ആകെ ചെലവ് (നെറ്റ്)',
  admJoined: 'ചേർന്ന തീയതി',
  admRegistered: 'രജിസ്റ്റർ ചെയ്തത്',
  admRegisteredOn: '{date}-ന് രജിസ്റ്റർ ചെയ്തു',
  admReceived: 'ലഭിച്ചത്',
  admSlot: 'സമയം',
  admPayment: 'പേയ്‌മെന്റ്',
  admAnyStatus: 'ഏത് നിലയും',
  admActive: 'സജീവം',
  admSuspended: 'നിർത്തിവച്ചത്',
  admNoDate: 'തീയതിയില്ല',
  admDocument: 'രേഖ',
  admNumber: 'നമ്പർ:',
  admAuthority: 'അധികാരി:',
  admUploaded: 'അപ്‌ലോഡ് ചെയ്തത്:',
  admGuest: 'അതിഥി',
  admActor: 'ചെയ്തയാൾ',
  admAction: 'നടപടി',
  admEntity: 'ഇനം',
  admDetails: 'വിശദാംശങ്ങൾ',
  admUnknown: 'അറിയില്ല',
  admUnknownRole: 'റോൾ അറിയില്ല',
  admCurrently: 'നിലവിൽ',

  // searches + empty states
  admSearchManagers: 'പേര്, മൊബൈൽ, ഇമെയിൽ അല്ലെങ്കിൽ വിലാസം',
  admSearchUsers: 'പേര്, മൊബൈൽ അല്ലെങ്കിൽ ഇമെയിൽ',
  admSearchBookings: 'വേദി, അതിഥി അല്ലെങ്കിൽ മൊബൈൽ',
  admSearchVenues: 'വേദി, വിഭാഗം, നഗരം അല്ലെങ്കിൽ മാനേജർ',
  admNoManagerMatch: 'ആ തിരയലിന് അനുയോജ്യമായ മാനേജർമാരില്ല.',
  admNoAccountMatch: 'ആ ഫിൽട്ടറുകൾക്ക് അനുയോജ്യമായ അക്കൗണ്ടുകളില്ല.',
  admNoBookingMatch: 'ആ ഫിൽട്ടറുകൾക്ക് അനുയോജ്യമായ ബുക്കിംഗുകളില്ല.',
  admNoVenueMatch: 'ആ ഫിൽട്ടറുകൾക്ക് അനുയോജ്യമായ വേദികളില്ല.',
  admManagerCount: { one: '{count} മാനേജർ', other: '{count} മാനേജർമാർ' },
  admAccountCount: { one: '{count} അക്കൗണ്ട്', other: '{count} അക്കൗണ്ടുകൾ' },

  // manager decisions
  admDocuments: 'രേഖകൾ',
  admVenueDocuments: 'വേദിയുടെ രേഖകൾ',
  admNoVenuesYet: 'ഇതുവരെ വേദികളൊന്നും ചേർത്തിട്ടില്ല.',
  admRemove: 'നീക്കം ചെയ്യുക',
  admRemoveAccount: 'അക്കൗണ്ട് നീക്കം ചെയ്യുക',
  admRemoveTitle: '{name} നീക്കം ചെയ്യണോ?',
  admRemoveManagerBody: '{name} എന്നവരുടെ അക്കൗണ്ട് ({phone}) അടയ്ക്കുകയും വിവരങ്ങൾ മായ്ക്കുകയും ചെയ്യും. അവരുടെ വേദികൾ സൈറ്റിൽ നിന്ന് മാറും, അവർ ചേർത്ത സൂപ്പർവൈസർമാർക്കും ഇനി ലോഗിൻ ചെയ്യാനാവില്ല. ആ ഫോൺ നമ്പർ ഉപയോഗിച്ച് വീണ്ടും രജിസ്റ്റർ ചെയ്യാനാകും. പഴയ ബുക്കിംഗുകൾ നിലനിൽക്കും. ഇത് പിൻവലിക്കാനാവില്ല.',
  admAccountRemoved: 'അക്കൗണ്ട് നീക്കം ചെയ്തു',
  admRemoveUserBody: '{name} എന്നവരുടെ അക്കൗണ്ട് ({phone}) അടയ്ക്കുകയും വിവരങ്ങൾ മായ്ക്കുകയും ചെയ്യും. ആ ഫോൺ നമ്പർ ഉപയോഗിച്ച് വീണ്ടും രജിസ്റ്റർ ചെയ്യാനാകും. പഴയ ബുക്കിംഗുകളും അഭിപ്രായങ്ങളും നിലനിൽക്കും. ഇത് പിൻവലിക്കാനാവില്ല.',
  admIdProof: 'തിരിച്ചറിയൽ രേഖ',
  admNoIdProof: 'ഇല്ല',
  admNoIdProofTip: 'തിരിച്ചറിയൽ രേഖ നിർബന്ധമാക്കുന്നതിന് മുൻപ് രജിസ്റ്റർ ചെയ്തത്',
  admNoIdProofBody:
    'തിരിച്ചറിയൽ രേഖ ഇല്ല — തിരിച്ചറിയൽ പരിശോധന നിർബന്ധമാക്കുന്നതിന് മുൻപാണ് ഈ മാനേജർ രജിസ്റ്റർ ചെയ്തത്. അംഗീകരിക്കുന്നതിന് മുൻപ് അവരോട് നേരിട്ട് ചോദിച്ചു വാങ്ങുക.',
  admReview: 'പരിശോധിക്കുക',
  admApproving: 'അംഗീകരിക്കുന്നു…',
  admApproveManager: 'മാനേജറെ അംഗീകരിക്കുക',
  admManagerApproved: 'മാനേജറെ അംഗീകരിച്ചു',
  admManagerSuspended: 'മാനേജറെ നിർത്തിവച്ചു — അവരെ സൈൻ ഔട്ട് ചെയ്തിട്ടുണ്ട്',
  admSuspend: 'നിർത്തിവയ്ക്കുക',
  admSuspending: 'നിർത്തിവയ്ക്കുന്നു…',
  admSuspendAccount: 'അക്കൗണ്ട് നിർത്തിവയ്ക്കുക',
  admSuspendTitle: '{name}-നെ നിർത്തിവയ്ക്കുക',
  admSuspendBody:
    'ഇത് {name}-നെ എല്ലായിടത്തുനിന്നും ഉടൻ സൈൻ ഔട്ട് ചെയ്യുകയും സൈൻ ഇൻ തടയുകയും ചെയ്യും. അടുത്ത തവണ ലോഗിൻ ചെയ്യാൻ ശ്രമിക്കുമ്പോൾ താഴെയുള്ള കാരണം അവർ കാണും, അതിനെതിരെ അപ്പീൽ നൽകാം.',
  admSuspendReason: 'നിർത്തിവയ്ക്കാനുള്ള കാരണം (നിർബന്ധം)',
  admSuspendHintManager: 'ഉദാ. വേദിയുടെ ലൈസൻസിന്റെ കാലാവധി കഴിഞ്ഞു, രണ്ട് തവണ ഓർമ്മിപ്പിച്ചിട്ടും പുതുക്കിയില്ല.',
  admSuspendHintUser: 'ഉദാ. സ്ഥിരീകരിച്ച ബുക്കിംഗുകൾക്ക് പലതവണ എത്തിയില്ല.',
  admSuspendNote: 'അവർക്ക് വായിക്കാനാണ് ഇത് — അവർക്ക് കിട്ടുന്ന ഒരേയൊരു വിശദീകരണം ഇതാണ്.',
  admReactivate: 'വീണ്ടും സജീവമാക്കുക',
  admReactivated: 'അക്കൗണ്ട് വീണ്ടും സജീവമാക്കി',
  admAccountSuspended: 'അക്കൗണ്ട് നിർത്തിവച്ചു — അവരെ സൈൻ ഔട്ട് ചെയ്തിട്ടുണ്ട്',

  // bookings
  admCancelled: 'റദ്ദാക്കി',
  admCancelledRefund: 'റദ്ദാക്കി · {amount} തിരികെ നൽകി',
  admCancelPrompt: '{date}-ന് {guest}-നായുള്ള {venue} ബുക്കിംഗ് റദ്ദാക്കണോ?',
  admCancelRefundsAll: 'അതിഥിക്ക് മുഴുവൻ {amount}-ഉം തിരികെ ലഭിക്കും, അതിഥിയെ അറിയിക്കുകയും ചെയ്യും.',
  admCancelNothingPaid: 'പണമൊന്നും അടച്ചിട്ടില്ല, അതിനാൽ തിരികെ നൽകാനില്ല. അതിഥിയെ അറിയിക്കും.',
  admKeepIt: 'നിലനിർത്തുക',

  // enquiries + messages
  admMarked: '{status} എന്ന് അടയാളപ്പെടുത്തി',
  admMarkAs: '{status} എന്ന് അടയാളപ്പെടുത്തുക',
  admMarkHandled: 'കൈകാര്യം ചെയ്തതായി അടയാളപ്പെടുത്തുക',
  admMarkedHandled: 'കൈകാര്യം ചെയ്തതായി അടയാളപ്പെടുത്തി',
  admReopen: 'വീണ്ടും തുറക്കുക',
  admReopened: 'വീണ്ടും തുറന്നു',
  admReply: 'മറുപടി',
  admYourMessage: 'നിങ്ങളുടെ സന്ദേശം',
  admNoSubject: 'വിഷയമില്ല',
  admNoMessages: 'ഇതുവരെ സന്ദേശങ്ങളില്ല. "ബന്ധപ്പെടുക" പേജിൽ നിന്നുള്ള സന്ദേശങ്ങളും സസ്പെൻഷൻ അപ്പീലുകളും ഇവിടെ വരും.',
  admReinstate: 'അക്കൗണ്ട് പുനഃസ്ഥാപിക്കുക',
  admAccountRestored: 'അക്കൗണ്ട് പുനഃസ്ഥാപിച്ചു — അവർക്ക് വീണ്ടും സൈൻ ഇൻ ചെയ്യാം',
  admDeclineAppeal: 'അപ്പീൽ നിരസിക്കുക',
  admDeclining: 'നിരസിക്കുന്നു…',
  admAppealDeclined: 'അപ്പീൽ നിരസിച്ചു — സൈൻ ഇൻ ചെയ്യുമ്പോൾ അവർ നിങ്ങളുടെ കാരണം കാണും',
  admDeclineBody:
    'അക്കൗണ്ട് നിർത്തിവച്ചതായി തുടരും. അടുത്ത തവണ സൈൻ ഇൻ ചെയ്യാൻ ശ്രമിക്കുമ്പോൾ {name} ഈ കുറിപ്പ് കാണും, ഒരിക്കൽ കൂടി അപ്പീൽ നൽകാം — അതിനാൽ എന്ത് ചെയ്താൽ തീരുമാനം മാറുമെന്ന് വ്യക്തമായി പറയുക.',
  admDeclineReason: 'അപ്പീൽ നിരസിക്കാനുള്ള കാരണം (നിർബന്ധം)',
  admDeclineHint: 'ഉദാ. നിങ്ങൾ ചേർത്തത് ഇപ്പോഴും കാലാവധി കഴിഞ്ഞ ലൈസൻസാണ് — പുതുക്കിയ പകർപ്പ് അയയ്ക്കുക.',
  admNoLongerSuspended: 'ഈ അക്കൗണ്ട് ഇപ്പോൾ നിർത്തിവച്ചിട്ടില്ല.',

  // venue moderation
  admVerification: 'പരിശോധന',
  admListing: 'ലിസ്റ്റിംഗ്',
  admListed: 'ലിസ്റ്റ് ചെയ്തു',
  admVerify: 'പരിശോധിക്കുക',
  admVerifying: 'പരിശോധിക്കുന്നു…',
  admVerifyVenue: 'വേദി പരിശോധിച്ച് അംഗീകരിക്കുക',
  admVerifyIntro:
    'താഴെയുള്ള ഒക്യുപ്പൻസി സർട്ടിഫിക്കറ്റും പബ്ലിക് അമ്യൂസ്‌മെന്റ് ലൈസൻസും പരിശോധിച്ച്, ഈ വേദി പൊതു ഉപയോഗത്തിന് നിയമപരമായി അനുമതിയുള്ളതാണെന്ന് ഉറപ്പാക്കുക.',
  admRejecting: 'നിരസിക്കുന്നു…',
  admConfirmRejection: 'നിരസിക്കൽ സ്ഥിരീകരിക്കുക',
  admRejectReason: 'നിരസിക്കാനുള്ള കാരണം (മാനേജർ കാണും)',
  admRejectReasonHint:
    'ഉദാ. ഒക്യുപ്പൻസി സർട്ടിഫിക്കറ്റിന്റെ കാലാവധി കഴിഞ്ഞു / വായിക്കാനാവുന്നില്ല — ശരിയായ പകർപ്പ് വീണ്ടും അപ്‌ലോഡ് ചെയ്യുക.',
  admRejectReasonRequired: 'നിരസിക്കാനുള്ള കാരണം നൽകുക.',
  admVenueVerified: 'വേദി പരിശോധിച്ചു — ഇനി സൈറ്റിൽ ലഭ്യമാക്കാം',
  admVenueRejected: 'വേദി നിരസിച്ചു',
  admVenueListed: 'വേദി ലിസ്റ്റ് ചെയ്തു',
  admVenueDelisted: 'വേദി ലിസ്റ്റിൽ നിന്ന് നീക്കി',
  admDelist: 'ലിസ്റ്റിൽ നിന്ന് നീക്കുക',
  admRelist: 'വീണ്ടും ലിസ്റ്റ് ചെയ്യുക',
  admShowAll: 'എല്ലാം കാണിക്കുക',
  admShowPending: 'കാത്തിരിക്കുന്നവ മാത്രം',
  admVenueCountFiltered: '{total} വേദികളിൽ {shown} എണ്ണം',
  admVisibleOnBrowse: { one: 'അതിഥികൾക്ക് {count} എണ്ണം കാണാം', other: 'അതിഥികൾക്ക് {count} എണ്ണം കാണാം' },
  admAwaitingVerification: {
    one: '{count} എണ്ണം പരിശോധന കാത്തിരിക്കുന്നു',
    other: '{count} എണ്ണം പരിശോധന കാത്തിരിക്കുന്നു',
  },
  admDocCount: { one: '{count} രേഖ', other: '{count} രേഖകൾ' },
  admLoadingDocuments: 'രേഖകൾ ലോഡ് ചെയ്യുന്നു…',
  admNoDocuments: 'രേഖകളൊന്നും ഇല്ല. രേഖകൾ നിർബന്ധമാക്കുന്നതിന് മുൻപ് ചേർത്ത വേദിയാണിത്.',
  admDocOccupancy: 'കെട്ടിട പൂർത്തീകരണ / ഒക്യുപ്പൻസി സർട്ടിഫിക്കറ്റ് (CC/OC)',
  admDocLicense: 'പ്രിമൈസസ് / പബ്ലിക് അമ്യൂസ്‌മെന്റ് ലൈസൻസ്',
  admLastDecided: 'അവസാന തീരുമാനം {date}',
  admPreviousRejection: 'മുൻപത്തെ നിരസിക്കൽ:',

  // review moderation
  admLoadingReviews: 'അഭിപ്രായങ്ങൾ ലോഡ് ചെയ്യുന്നു…',
  admNoReviews: 'ഈ വേദിക്ക് അഭിപ്രായങ്ങളില്ല.',
  admReviewModerationNote:
    'ഉള്ളടക്ക നിയമങ്ങൾ ലംഘിക്കുന്ന അഭിപ്രായങ്ങൾ നീക്കം ചെയ്യുക. അതിഥിയെ അറിയിക്കും, ഇത് ഓഡിറ്റ് ലോഗിൽ രേഖപ്പെടുത്തും. ഇത് പിൻവലിക്കാനാവില്ല.',
  admRemoving: 'നീക്കം ചെയ്യുന്നു…',
  admConfirmRemoval: 'നീക്കം ചെയ്യൽ സ്ഥിരീകരിക്കുക',
  admReviewRemoved: 'അഭിപ്രായം നീക്കം ചെയ്തു',

  // ------------------------------------------------------------- role labels
  roleUser: 'ഉപയോക്താവ്',
  roleManager: 'മാനേജർ',
  roleAdmin: 'അഡ്മിൻ',
  roleSupervisor: 'സൂപ്പർവൈസർ',

  // Offline payments (money received at the venue, recorded by staff)
  payPaidFull: 'മുഴുവൻ തുകയും അടച്ചു',
  payReceived: '{paid} ലഭിച്ചു',
  payBalanceDue: 'ബാക്കി {amount} അടയ്ക്കാനുണ്ട്',
  payFullDue: '{amount} വേദിയിൽ അടയ്ക്കാനുണ്ട്',
  payRefunded: 'പണം തിരികെ നൽകി',
  payRecord: 'പേയ്\u200cമെന്റ് രേഖപ്പെടുത്തുക',
  payRecordTitle: 'പേയ്\u200cമെന്റ് രേഖപ്പെടുത്തുക',
  payTotal: 'ബുക്കിംഗ് തുക',
  payReceivedSoFar: 'ഇതുവരെ ലഭിച്ചത്',
  payBalance: 'ബാക്കി',
  payAmount: 'ലഭിച്ച തുക (₹)',
  payAmountRequired: 'ലഭിച്ച തുക നൽകുക',
  payOverBalance: 'ഇത് ബാക്കിയുള്ള {amount}-നേക്കാൾ കൂടുതലാണ്',
  payFillBalance: 'മുഴുവൻ ബാക്കി {amount}',
  payMethod: 'അടച്ച രീതി',
  payMethod_cash: 'ക്യാഷ്',
  payMethod_upi: 'UPI',
  payMethod_bank: 'ബാങ്ക് ട്രാൻസ്ഫർ',
  payMethod_card: 'കാർഡ് (വേദിയിൽ)',
  payMethod_other: 'മറ്റുള്ളവ',
  payReference: 'റഫറൻസ്',
  payReferenceHint: 'UPI / ബാങ്ക് ട്രാൻസാക്ഷൻ ID, ചെക്ക് നമ്പർ…',
  paySave: 'പേയ്\u200cമെന്റ് സേവ് ചെയ്യുക',
  paySavedFull: 'പേയ്\u200cമെന്റ് രേഖപ്പെടുത്തി — മുഴുവൻ തുകയും അടച്ചു',
  paySavedBalance: 'പേയ്\u200cമെന്റ് രേഖപ്പെടുത്തി — ഇനി {amount} ബാക്കി',

  // Venue equipment shown to guests
  eqNone: 'വേദി ഇതുവരെ ഉപകരണങ്ങൾ ചേർത്തിട്ടില്ല.',
  eqCount: '{name} × {count}',
  eqPartial: '{name}: {total}-ൽ {available} എണ്ണം ലഭ്യം ({count} {status})',
  eqOut: '{name} — {status}',
  eqNoticeTitle: 'ഇപ്പോൾ പൂർണ്ണമായി ലഭ്യമല്ല',
  eqNoticeAsk: 'നിങ്ങളുടെ പരിപാടിക്ക് ഇവ ആവശ്യമെങ്കിൽ, ബുക്ക് ചെയ്യുന്നതിന് മുൻപ് വേദിയോട് ചോദിക്കുക.',

  // Manager bookings table labels
  mgrBookedFor: 'തീയതി',
  mgrBookedOn: 'ബുക്ക് ചെയ്തത്',
  mgrPayment: 'പേയ്\u200cമെന്റ്',
  mgrReceipt: 'രസീത്',
  mgrTxn: 'ഇടപാട്',
  mgrPaidAtVenue: 'വേദിയിൽ വാങ്ങി',
  mgrPaidOnlineLegacy: 'ഓൺലൈനായി അടച്ചു (പഴയ ബുക്കിംഗ്)',
  mgrPaid: 'അടച്ചത്',
};

export const ml: Partial<Record<keyof typeof en, Phrase>> = {
  ...base, ...coreMl, ...homeMl, ...exploreMl, ...venueMl, ...bookingMl, ...pagesMl, ...staffMl,
};
